/**
 * Quiz Player Component
 * Allows students to take quizzes
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FiClock, FiSave, FiCheckCircle, FiArrowLeft, FiArrowRight, FiBookOpen, FiUser, FiCalendar, FiFileText, FiClock as FiTime, FiChevronDown, FiChevronUp, FiAlertCircle, FiRefreshCw, FiInfo } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';
import apiService from '../../services/api';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import QuizSaveManager from '../../utils/quizSaveManager';
import tokenRefreshManager from '../../utils/tokenRefreshManager';
import logger from '../../utils/logger';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  
  /* Prevent text selection and copying */
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  
  /* Prevent drag and drop */
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
  
  /* Prevent image dragging */
  img {
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    user-drag: none;
  }
  
  /* Ensure proper Arabic text rendering */
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans Arabic', sans-serif;
  direction: ltr;
  
  /* Arabic text specific styling */
  * {
    unicode-bidi: embed;
  }
  
  /* Ensure Arabic text displays properly */
  [dir="rtl"], .arabic-text {
    direction: rtl;
    text-align: right;
    unicode-bidi: bidi-override;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
  }
  
  /* Mobile viewport optimizations */
  @media (max-width: 768px) {
    /* Prevent horizontal scroll on mobile */
    overflow-x: hidden;
    /* Ensure proper touch scrolling */
    -webkit-overflow-scrolling: touch;
    /* Prevent zoom on input focus (iOS) */
    font-size: 16px;
    /* Safe area support for notched devices (iPhone X+ through iPhone 17 Pro Max) */
    padding-left: max(1rem, env(safe-area-inset-left));
    padding-right: max(1rem, env(safe-area-inset-right));
  }
  
  /* Large iPhone models (iPhone 15 Pro Max, iPhone 16 Pro Max, iPhone 17 Pro Max - 430px) */
  @media (max-width: 430px) {
    padding-left: max(0.875rem, env(safe-area-inset-left));
    padding-right: max(0.875rem, env(safe-area-inset-right));
  }
  
  @media (max-width: 480px) {
    /* Safe area support for smaller devices */
    padding-left: max(0.75rem, env(safe-area-inset-left));
    padding-right: max(0.75rem, env(safe-area-inset-right));
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  box-sizing: border-box;
  
  /* Tablet and smaller */
  @media (max-width: 1024px) {
    margin-bottom: 1.75rem;
  }
  
  /* Mobile landscape and smaller */
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
  
  /* Mobile portrait */
  @media (max-width: 480px) {
    padding: 0.75rem;
    margin-bottom: 1.25rem;
    gap: 0.75rem;
  }
  
  /* Small mobile devices */
  @media (max-width: 360px) {
    padding: 0.625rem;
    margin-bottom: 1rem;
    gap: 0.5rem;
  }
`;

const QuizTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 1.375rem;
  }
  
  @media (max-width: 360px) {
    font-size: 1.25rem;
  }
`;

const QuizDescription = styled.p`
  font-size: 1rem;
  font-weight: 400;
  color: var(--text-secondary);
  margin: 0.5rem 0 0 0;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 0.9375rem;
  }
  
  @media (max-width: 360px) {
    font-size: 0.875rem;
  }
`;

const Timer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: ${props => {
    if (props.warning) return 'rgba(246, 224, 94, 0.1)';
    if (props.danger) return 'rgba(252, 129, 129, 0.1)';
    return 'var(--background-secondary)';
  }};
  color: ${props => {
    if (props.warning) return 'var(--warning-dark)';
    if (props.danger) return 'var(--error-color)';
    return 'var(--text-primary)';
  }};
  border: 2px solid ${props => {
    if (props.warning) return 'var(--warning-color)';
    if (props.danger) return 'var(--error-color)';
    return 'var(--border-color)';
  }};
  border-radius: 0;
  font-weight: 600;
  font-size: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    justify-content: center;
    font-size: 0.9rem;
    padding: 0.75rem 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.5rem 0.75rem;
    /* Better text rendering on mobile */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: var(--background-tertiary);
  border-radius: 0;
  overflow: hidden;
  margin-bottom: 2rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const QuestionCard = styled.div`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: var(--shadow-md);
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const SaveStatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  border-radius: 0;
  background: ${props => {
    if (props.status === 'saving') return 'rgba(246, 224, 94, 0.1)';
    if (props.status === 'saved') return 'rgba(104, 211, 145, 0.1)';
    if (props.status === 'failed') return 'rgba(252, 129, 129, 0.1)';
    return 'var(--background-secondary)';
  }};
  color: ${props => {
    if (props.status === 'saving') return 'var(--warning-dark)';
    if (props.status === 'saved') return 'var(--success-dark)';
    if (props.status === 'failed') return 'var(--error-color)';
    return 'var(--text-muted)';
  }};
  
  svg {
    flex-shrink: 0;
  }
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  background: var(--error-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: var(--error-dark);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const NetworkStatusIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => {
    if (props.status === 'offline') return 'rgba(252, 129, 129, 0.1)';
    if (props.status === 'unstable') return 'rgba(246, 224, 94, 0.1)';
    return 'rgba(104, 211, 145, 0.1)';
  }};
  color: ${props => {
    if (props.status === 'offline') return 'var(--error-color)';
    if (props.status === 'unstable') return 'var(--warning-dark)';
    return 'var(--success-dark)';
  }};
  border: 1px solid ${props => {
    if (props.status === 'offline') return 'var(--error-color)';
    if (props.status === 'unstable') return 'var(--warning-color)';
    return 'var(--success-color)';
  }};
  
  svg {
    flex-shrink: 0;
  }
`;

const NetworkErrorBanner = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: ${props => {
    if (props.severity === 'critical') return 'rgba(252, 129, 129, 0.1)';
    if (props.severity === 'warning') return 'rgba(246, 224, 94, 0.1)';
    if (props.severity === 'info') return props.style?.backgroundColor || 'rgba(96, 165, 250, 0.1)';
    return 'rgba(96, 165, 250, 0.1)';
  }};
  color: ${props => {
    if (props.severity === 'critical') return 'var(--error-color)';
    if (props.severity === 'warning') return 'var(--warning-dark)';
    if (props.severity === 'info') return props.style?.color || 'var(--info-color)';
    return 'var(--info-color)';
  }};
  border-bottom: 2px solid ${props => {
    if (props.severity === 'critical') return 'var(--error-color)';
    if (props.severity === 'warning') return 'var(--warning-color)';
    if (props.severity === 'info') return props.style?.borderColor || 'var(--info-color)';
    return 'var(--info-color)';
  }};
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.9375rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    font-size: 0.875rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .banner-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    
    svg {
      flex-shrink: 0;
    }
  }
  
  .banner-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .dismiss-button {
    background: transparent;
    border: 1px solid currentColor;
    color: currentColor;
    padding: 0.375rem 0.75rem;
    border-radius: 0;
    cursor: pointer;
    font-size: 0.875rem;
    transition: background 0.2s;
    
    &:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }
`;

// UI FIX: Auto-submit overlay component
const AutoSubmitOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 1rem;
  
  .spinner {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  .message {
    color: var(--text-inverse);
    font-size: 1.25rem;
    font-weight: bold;
    text-align: center;
  }
  
  .submessage {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.875rem;
    text-align: center;
  }
`;

// UI FIX: Timer expired text with pulse animation
const TimerExpiredText = styled.span`
  color: var(--error-color);
  font-weight: bold;
  animation: pulse 1s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const QuestionNumber = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const QuestionPoints = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-inverse);
  background: var(--contrast-accent);
  padding: 0.375rem 0.875rem;
  border-radius: 0;
  border: 1px solid var(--contrast-accent);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ExtraCreditBadge = styled.span`
  display: inline-block;
  margin-left: 0.75rem;
  padding: 0.25rem 0.5rem;
  background: linear-gradient(135deg, var(--warning-color), var(--warning-dark));
  color: var(--text-inverse);
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 0;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);
  
  @media (max-width: 768px) {
    margin-left: 0.5rem;
    padding: 0.2rem 0.4rem;
    font-size: 0.7rem;
  }
  
  @media (max-width: 480px) {
    margin-left: 0.25rem;
    padding: 0.15rem 0.3rem;
    font-size: 0.65rem;
  }
`;

const QuestionPrompt = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const OptionItem = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--background-primary);
  min-height: 44px; /* Ensure minimum touch target size */
  box-sizing: border-box;

  &:hover {
    border-color: var(--border-dark);
    background: var(--background-secondary);
  }

  &.selected {
    border-color: var(--primary-color);
    background: rgba(246, 224, 94, 0.1);
  }
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    padding: 0.875rem;
    gap: 0.625rem;
    min-height: 48px; /* Larger touch target on mobile */
    /* Better touch feedback */
    -webkit-tap-highlight-color: rgba(216, 184, 137, 0.2);
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.5rem;
    min-height: 44px;
  }
`;

const OptionInput = styled.input`
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  accent-color: var(--primary-color);
  cursor: pointer;
  flex-shrink: 0;
  
  /* Mobile optimizations - ensure touch target is large enough */
  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
  }
  
  @media (max-width: 480px) {
    width: 22px;
    height: 22px;
    min-width: 22px;
    min-height: 22px;
  }
`;

const OptionText = styled.span`
  flex: 1;
  font-size: 1rem;
  color: var(--text-primary);
`;

const OptionInstruction = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0 0 1rem 0;
  font-style: italic;
  padding: 0.5rem;
  background: var(--background-secondary);
  border-radius: 0;
  border-left: 3px solid var(--primary-color);
`;

const FillBlankInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: var(--background-primary);
  box-sizing: border-box;
  
  /* Allow text selection in input fields */
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 16px; /* Prevent iOS zoom on focus */
    min-height: 44px; /* Ensure minimum touch target */
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 16px; /* Ensure 16px to prevent iOS zoom */
  }
`;

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    margin-top: 1.5rem;
  }
  
  @media (max-width: 1024px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  max-width: 100%;
  overflow-x: auto;
  min-width: 0;
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    order: 2;
  }
  
  @media (max-width: 480px) {
    gap: 0.25rem;
  }
  
  @media (max-width: 1024px) {
    order: 2;
    width: 100%;
    margin: 0.5rem 0;
  }
`;

const PaginationButton = styled.button`
  width: 32px;
  height: 32px;
  border: 2px solid ${props => {
    if (props.$active) return 'var(--primary-color)';
    if (props.$visited && props.$answered) return 'rgba(104, 211, 145, 0.2)';
    if (props.$visited && props.$answered === false) return 'rgba(246, 224, 94, 0.2)';
    return 'var(--border-color)';
  }};
  background: ${props => {
    if (props.$active) return 'var(--primary-color)';
    if (props.$visited && props.$answered) return 'rgba(104, 211, 145, 0.1)';
    if (props.$visited && props.$answered === false) return 'rgba(246, 224, 94, 0.1)';
    return 'var(--background-primary)';
  }};
  color: ${props => {
    if (props.$active) return 'var(--text-inverse)';
    if (props.$visited && props.$answered) return 'var(--success-dark)';
    if (props.$visited && props.$answered === false) return 'var(--warning-dark)';
    return 'var(--text-secondary)';
  }};
  border-radius: 0;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  flex-shrink: 0;
  position: relative;
  
  &:hover {
    border-color: ${props => {
      if (props.$active) return 'var(--primary-dark)';
      if (props.$visited && props.$answered) return 'var(--success-dark)';
      if (props.$visited && props.$answered === false) return 'var(--warning-dark)';
      return 'var(--primary-color)';
    }};
    background: ${props => {
      if (props.$active) return 'var(--primary-dark)';
      if (props.$visited && props.$answered) return 'rgba(104, 211, 145, 0.15)';
      if (props.$visited && props.$answered === false) return 'rgba(246, 224, 94, 0.15)';
      return 'rgba(246, 224, 94, 0.1)';
    }};
    color: ${props => {
      if (props.$active) return 'var(--text-inverse)';
      if (props.$visited && props.$answered) return 'var(--success-dark)';
      if (props.$visited && props.$answered === false) return 'var(--warning-dark)';
      return 'var(--text-secondary)';
    }};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 0.875rem;
    /* Ensure minimum touch target size for mobile */
    min-width: 44px;
    min-height: 44px;
  }
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid var(--border-color);
  background: var(--background-primary);
  color: var(--text-secondary);
  border-radius: 0;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: var(--background-secondary);
    border-color: var(--border-dark);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 1024px) {
    order: 1;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 1rem 1.5rem;
    font-size: 1rem;
    order: 1;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.9rem;
    /* Ensure minimum touch target size for mobile */
    min-height: 44px;
    /* Better touch feedback */
    -webkit-tap-highlight-color: rgba(216, 184, 137, 0.2);
  }

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);

    &:hover:not(:disabled) {
      background: var(--primary-dark);
    }
  }

  &.success {
    background: var(--success-color);
    color: var(--text-inverse);
    border-color: var(--success-color);

    &:hover:not(:disabled) {
      background: var(--success-dark);
    }
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  background: var(--background-primary);
  color: var(--text-secondary);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-secondary);
    border-color: var(--border-dark);
  }

  &.saved {
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
    border-color: var(--success-color);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--error-color);
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: var(--shadow-md);
  margin: 2rem 0;
`;

const ErrorIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ErrorTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--error-color);
  margin: 0 0 1rem 0;
`;

const ErrorMessage = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  margin: 0 0 1.5rem 0;
  line-height: 1.5;
`;

const ErrorAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &:hover {
    background: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(216, 184, 137, 0.3);
  }
`;

// Review Page Components
const ReviewContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const ReviewHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem;
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: var(--shadow-md);
`;

const ReviewTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ReviewSubtitle = styled.p`
  font-size: 1.125rem;
  color: var(--text-secondary);
  margin: 0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.6;
`;


const ReviewQuestion = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: ${props => props.unanswered ? 'rgba(252, 129, 129, 0.1)' : 'var(--background-primary)'};
  border-radius: 0;
  border-left: 4px solid ${props => props.unanswered ? 'var(--error-color)' : 'var(--primary-color)'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-1px);
  }
`;

const ReviewQuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;


const ReviewQuestionPoints = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  background: transparent;
  padding: 0;
  border-radius: 0;
  border: none;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    white-space: nowrap;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const ReviewQuestionPrompt = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  line-height: 1.6;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ReviewQuestionPromptWithHTML = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  line-height: 1.6;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  /* Style for fill-in-the-blank answers */
  .fill-blank-answer {
    background: rgba(246, 224, 94, 0.1);
    color: var(--warning-dark);
    padding: 0.25rem 0.5rem;
    border-radius: 0;
    border: 1px solid var(--primary-color);
    font-weight: 600;
    margin: 0 0.25rem;
  }
`;

const ReviewAnswer = styled.div`
  margin-top: 1rem;
`;

const ReviewAnswerText = styled.div`
  font-size: 1rem;
  color: var(--text-primary);
  background: var(--background-secondary);
  padding: 1rem;
  border-radius: 0;
  border: 1px solid var(--border-color);
  line-height: 1.6;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const ReviewAnswerEmpty = styled.div`
  font-size: 1rem;
  color: var(--error-color);
  font-style: italic;
  background: rgba(252, 129, 129, 0.1);
  padding: 1rem;
  border-radius: 0;
  border: 1px solid var(--error-color);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.6;
`;

const ReviewOptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ReviewOption = styled.div`
  padding: 0.75rem;
  border-radius: 0;
  border: 2px solid ${props => props.selected ? 'var(--primary-color)' : 'var(--border-color)'};
  background: ${props => props.selected ? 'rgba(246, 224, 94, 0.1)' : 'var(--background-primary)'};
  color: ${props => props.selected ? 'var(--warning-dark)' : 'var(--text-primary)'};
  font-weight: ${props => props.selected ? '600' : '500'};
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  transition: all 0.2s ease;
  
  ${props => props.selected && `
    &::before {
      content: '✓';
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
      color: var(--primary-color);
      font-weight: bold;
      font-size: 1.1rem;
    }
  `}
  
  &:hover {
    border-color: ${props => props.selected ? 'var(--primary-color)' : 'var(--border-dark)'};
    background: ${props => props.selected ? 'rgba(246, 224, 94, 0.1)' : 'var(--background-secondary)'};
  }
`;

const ReviewActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1.5rem;
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    padding: 1.25rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    gap: 0.625rem;
  }
`;

const ReviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.875rem 1.75rem;
  border: 2px solid var(--border-color);
  background: var(--background-primary);
  color: var(--text-secondary);
  border-radius: 0;
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  flex: 1;
  min-height: 44px;
  box-sizing: border-box;

  &:hover:not(:disabled) {
    background: var(--background-secondary);
    border-color: var(--border-dark);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);

    &:hover:not(:disabled) {
      background: var(--primary-dark);
      border-color: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }

  &.secondary {
    background: var(--background-primary);
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:hover:not(:disabled) {
      background: var(--background-secondary);
      border-color: var(--border-dark);
      transform: translateY(-1px);
      box-shadow: var(--shadow-sm);
    }
  }

  &.success {
    background: var(--success-color);
    color: var(--text-inverse);
    border-color: var(--success-color);

    &:hover:not(:disabled) {
      background: var(--success-dark);
      border-color: var(--success-dark);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0.875rem 1.5rem;
    font-size: 0.9375rem;
    min-height: 48px;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    min-height: 44px;
  }
`;

const EditLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.875rem;
  border: 2px solid var(--contrast-accent);
  background: var(--contrast-accent);
  color: var(--text-inverse);
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  min-height: auto;
  height: auto;

  &:hover {
    background: var(--background-primary);
    border-color: var(--contrast-accent);
    color: var(--contrast-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(26, 55, 93, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.3rem 0.75rem;
    font-size: 0.8rem;
    min-width: auto;
    white-space: nowrap;
  }
  
  @media (max-width: 480px) {
    padding: 0.25rem 0.6rem;
    font-size: 0.75rem;
  }
`;



const QuizDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin: 1rem auto 0 auto;
  padding: 1rem;
  background: linear-gradient(135deg, var(--background-secondary) 0%, var(--background-tertiary) 100%);
  border-radius: 0;
  border: 1px solid rgba(216, 184, 137, 0.1);
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  max-width: 800px;
  width: 100%;
  box-sizing: border-box;
  
  /* Tablet and smaller */
  @media (max-width: 1024px) {
    max-width: 90%;
    margin: 1rem auto 0 auto;
  }
  
  /* Mobile landscape and smaller */
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin: 0.75rem auto 0 auto;
    padding: 0.875rem;
    font-size: 0.875rem;
    max-width: 95%;
  }
  
  /* Mobile portrait */
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.8125rem;
    gap: 0.5rem;
    margin: 0.5rem auto 0 auto;
    max-width: 98%;
  }
  
  /* Small mobile devices */
  @media (max-width: 360px) {
    padding: 0.625rem;
    font-size: 0.75rem;
    gap: 0.5rem;
    margin: 0.375rem auto 0 auto;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 400;
  color: var(--text-secondary);
  padding: 0.25rem 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  svg {
    color: var(--primary-color);
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
  
  span {
    line-height: 1.4;
    word-break: break-word;
    flex: 1;
  }
  
  /* Tablet and smaller */
  @media (max-width: 1024px) {
    gap: 0.5rem;
  }
  
  /* Mobile landscape and smaller */
  @media (max-width: 768px) {
    justify-content: flex-start;
    text-align: left;
    gap: 0.5rem;
    padding: 0.375rem 0;
    align-items: center;
  }
  
  /* Mobile portrait */
  @media (max-width: 480px) {
    gap: 0.5rem;
    font-size: 0.8125rem;
    padding: 0.375rem 0;
    flex-direction: row;
    align-items: center;
    text-align: left;
    justify-content: flex-start;
  }
  
  /* Small mobile devices */
  @media (max-width: 360px) {
    gap: 0.5rem;
    font-size: 0.75rem;
    padding: 0.25rem 0;
  }
`;

const CustomDropdown = styled.div`
  position: relative;
  flex: 1;
  z-index: 1;
  
  &:focus-within {
    z-index: 10000;
  }
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 0.95rem;
  background: var(--background-primary);
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  min-height: 48px;
  text-align: left;
  display: flex;
  align-items: center;
  color: ${props => props.disabled ? 'var(--text-muted)' : (props.hasValue ? 'var(--text-primary)' : 'var(--text-muted)')};
  transition: all 0.2s ease;
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover:not(:disabled) {
    border-color: var(--primary-color);
  }

  &:focus:not(:disabled) {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
  
  @media (max-width: 1024px) {
    padding: 0.6875rem;
    padding-right: 2.375rem;
    font-size: 0.9375rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem;
    padding-right: 2.25rem;
    font-size: 0.9rem;
    min-height: 44px;
    touch-action: manipulation; // Better touch handling
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    padding-right: 2rem;
    font-size: 0.85rem;
    min-height: 40px;
  }
  
  @media (max-width: 360px) {
    padding: 0.4375rem;
    padding-right: 1.875rem;
    font-size: 0.8125rem;
    min-height: 38px;
  }
`;

const DropdownIcon = styled.span`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    right: 0.625rem;
  }
  
  @media (max-width: 480px) {
    right: 0.5rem;
  }
`;

const DropdownMenu = styled.div.attrs(props => ({
  style: props.position ? {
    position: 'fixed',
    top: `${props.position.top}px`,
    left: `${props.position.left}px`,
    width: `${props.position.width}px`,
    maxWidth: 'calc(100vw - 2rem)',
  } : {}
}))`
  margin-top: 0.25rem;
  background: var(--background-primary);
  border: 2px solid var(--border-color);
  border-radius: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: visible;
  
  @media (max-width: 1024px) {
    max-height: 280px;
  }
  
  @media (max-width: 768px) {
    max-width: calc(100vw - 1rem) !important;
    max-height: 250px;
    font-size: 0.9rem;
    border-radius: 0;
  }
  
  @media (max-width: 480px) {
    max-width: calc(100vw - 0.5rem) !important;
    max-height: 200px;
    font-size: 0.85rem;
    border-radius: 0;
  }
  
  @media (max-width: 360px) {
    max-height: 180px;
    font-size: 0.8125rem;
  }
`;

const DropdownOption = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: var(--background-primary);
  text-align: left;
  cursor: pointer;
  font-size: 0.95rem;
  color: var(--text-primary);
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  transition: background 0.2s ease;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--background-secondary);
  }

  &.selected {
    background: rgba(246, 224, 94, 0.1);
    color: var(--text-primary);
  }
  
  @media (max-width: 1024px) {
    padding: 0.6875rem 0.9375rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 0.875rem;
    font-size: 0.9rem;
    min-height: 44px;
    touch-action: manipulation; // Better touch handling
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
    min-height: 40px;
  }
  
  @media (max-width: 360px) {
    padding: 0.4375rem 0.625rem;
    font-size: 0.8125rem;
    min-height: 38px;
  }
`;

const MatchingHeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 1024px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
    margin-bottom: 0.875rem;
  }
  
  h4 {
    @media (max-width: 768px) {
      font-size: 0.9375rem !important;
      margin-bottom: 0.75rem !important;
    }
    
    @media (max-width: 480px) {
      font-size: 0.875rem !important;
      margin-bottom: 0.5rem !important;
    }
  }
`;

const MatchingItemGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: var(--background-primary);
  border-radius: 0;
  border: 1px solid var(--border-color);
  align-items: center;
  
  @media (max-width: 1024px) {
    gap: 1.5rem;
    padding: 0.9375rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 0.875rem;
    margin-bottom: 0.875rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    border-radius: 0;
  }
  
  @media (max-width: 360px) {
    padding: 0.625rem;
    gap: 0.625rem;
  }
  
  > div:first-child {
    @media (max-width: 768px) {
      margin-bottom: 0.5rem;
      font-size: 0.9375rem;
      line-height: 1.5;
    }
    
    @media (max-width: 480px) {
      font-size: 0.875rem;
      margin-bottom: 0.375rem;
    }
    
    @media (max-width: 360px) {
      font-size: 0.8125rem;
    }
  }
`;

const getInstructorName = (assignment) => {
  if (assignment?.instructor_first_name && assignment?.instructor_last_name) {
    return `${assignment.instructor_first_name} ${assignment.instructor_last_name}`;
  } else if (assignment?.instructor_first_name) {
    return assignment.instructor_first_name;
  } else if (assignment?.instructor_last_name) {
    return assignment.instructor_last_name;
  }
  return 'Unknown Instructor';
};

function QuizPlayer() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRetakeMode = searchParams.get('retake') === 'true';
  const isEditMode = searchParams.get('edit') === 'true';

  const [attempt, setAttempt] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  
  // Refs for latest state (fixes stale closure issues)
  const responsesRef = useRef({});
  const setResponsesRef = useRef(null);
  
  // Save manager instance
  const saveManagerRef = useRef(null);
  
  // Debounce timers for immediate auto-save
  const debounceTimersRef = useRef({});
  
  // Timeout timers for clearing "saved" status after 5 seconds
  const savedStatusTimeoutsRef = useRef({});
  
  // Track manual saves to extend status timeout
  const manualSaveFlagsRef = useRef({});
  
  // FIX 1: Track component mount status to prevent state updates after unmount
  const isMountedRef = useRef(true);
  
  // FIX 2: Track periodic save timeout to prevent multiple timeouts
  const periodicSaveTimeoutRef = useRef(null);
  
  // FIX 3: Track timed quiz interval to prevent multiple intervals
  const timedSaveIntervalRef = useRef(null);
  
  // Track if save manager has been initialized with existing responses
  const hasInitializedSaveManagerRef = useRef(false);
  
  // Track last reset time to prevent rapid flickering from multiple resets
  const lastIndexResetRef = useRef(0);
  
  // Save status tracking (per question) - used by save manager callback
  // eslint-disable-next-line no-unused-vars
  const [saveStatuses, setSaveStatuses] = useState({}); // questionId -> { status, timestamp, error }
  
  // Check if any answers are currently being saved (for submit button disable)
  const hasUnsavedAnswers = useMemo(() => {
    // CRITICAL FIX: When on the last question, only check the current question
    // Previous questions can continue saving in the background without blocking review mode
    if (quiz && currentQuestionIndex === quiz.questions.length - 1) {
      const currentQuestion = quiz.questions[currentQuestionIndex];
      if (currentQuestion) {
        const currentStatus = saveStatuses[currentQuestion.id];
        const hasCurrentSaving = currentStatus?.status === 'saving';
        const hasCurrentTimer = debounceTimersRef.current[currentQuestion.id] !== undefined;
        const hasCurrentActiveSave = saveManagerRef.current?.hasActiveSave(currentQuestion.id) || false;
        
        return hasCurrentSaving || hasCurrentTimer || hasCurrentActiveSave;
      }
    }
    
    // For other questions, check all questions (original behavior)
    // Check save statuses for 'saving' status
    const hasSavingStatus = Object.values(saveStatuses).some(
      status => status?.status === 'saving'
    );
    
    // Check pending debounce timers
    const hasPendingTimers = Object.keys(debounceTimersRef.current).length > 0;
    
    // Check save manager active saves
    const hasActiveSaves = saveManagerRef.current?.hasActiveSaves() || false;
    
    return hasSavingStatus || hasPendingTimers || hasActiveSaves;
  }, [saveStatuses, quiz, currentQuestionIndex]);
  
  // Count unsaved answers for status message
  const unsavedCount = useMemo(() => {
    // CRITICAL FIX: When on the last question, only count the current question
    if (quiz && currentQuestionIndex === quiz.questions.length - 1) {
      const currentQuestion = quiz.questions[currentQuestionIndex];
      if (currentQuestion) {
        const currentStatus = saveStatuses[currentQuestion.id];
        const currentSaving = currentStatus?.status === 'saving' ? 1 : 0;
        const currentTimer = debounceTimersRef.current[currentQuestion.id] !== undefined ? 1 : 0;
        return Math.max(currentSaving, currentTimer);
      }
      return 0;
    }
    
    // For other questions, count all (original behavior)
    const savingCount = Object.values(saveStatuses).filter(
      status => status?.status === 'saving'
    ).length;
    
    const pendingTimerCount = Object.keys(debounceTimersRef.current).length;
    
    // Return the maximum count (most accurate)
    return Math.max(savingCount, pendingTimerCount);
  }, [saveStatuses, quiz, currentQuestionIndex]);
  
  // Helper function to validate if a response has a valid answer based on question type
  // CRITICAL: Only returns true if an answer is actually selected/entered (not empty)
  // FIX #7: Added explicit empty string checks and improved validation
  const hasValidAnswer = useCallback((questionType, response) => {
    if (!response) return false;
    
    // FIX #7: Helper to check if a value is a valid non-empty ID
    const isValidId = (id) => {
      if (id === null || id === undefined) return false;
      if (typeof id === 'number') return !isNaN(id);
      if (typeof id === 'string') return id.trim() !== '';
      return false;
    };
    
    if (questionType === 'multiple_choice') {
      // Check if any options are selected (support both new and old format)
      if (response.selected_option_ids && Array.isArray(response.selected_option_ids) && response.selected_option_ids.length > 0) {
        // FIX #7: Validate that at least one ID is valid
        return response.selected_option_ids.some(id => isValidId(id));
      }
      if (response.answer_text) {
        try {
          const parsed = JSON.parse(response.answer_text);
          if (Array.isArray(parsed) && parsed.length > 0) {
            // FIX #7: Validate that at least one ID is valid
            return parsed.some(id => isValidId(id));
          }
        } catch (e) {
          // Not JSON, fall through to legacy check
        }
      }
      // Legacy single selection - must have a valid selected_option_id
      // FIX #7: Use isValidId helper for consistent validation
      return isValidId(response.selected_option_id);
    } else if (questionType === 'true_false') {
      // Must have a selected option (valid ID)
      // FIX #7: Use isValidId helper for consistent validation
      return isValidId(response.selected_option_id);
    } else if (questionType === 'matching') {
      // Check if matching pairs exist in answer_text (must have at least one pair)
      if (response.answer_text) {
        try {
          const pairs = JSON.parse(response.answer_text);
          if (Array.isArray(pairs) && pairs.length > 0) {
            // FIX #7 & #12: Validate that pairs have valid structure and non-negative indices
            return pairs.some(pair =>
              pair &&
              typeof pair === 'object' &&
              typeof pair.leftIndex === 'number' &&
              typeof pair.rightIndex === 'number' &&
              pair.leftIndex >= 0 &&
              pair.rightIndex >= 0
            );
          }
        } catch (e) {
          // Not valid JSON
        }
      }
      return false;
    } else if (questionType === 'fill_blank') {
      // For fill_blank, check if answer_text has non-empty content
      if (response.answer_texts && Array.isArray(response.answer_texts)) {
        // Multiple blanks - at least one must have non-empty content
        return response.answer_texts.some(answer => answer && typeof answer === 'string' && answer.trim() !== '');
      }
      // Single blank - check answer_text has non-empty content
      // FIX #7: Ensure answer_text is not just whitespace
      return response.answer_text &&
             typeof response.answer_text === 'string' &&
             response.answer_text.trim() !== '';
    }
    
    return false;
  }, []);
  
  // Ref to avoid dependency issues with handleSubmit
  const handleSubmitRef = useRef();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [error, setError] = useState(null);
  const [firewallWarning, setFirewallWarning] = useState(null);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [dropdownPositions, setDropdownPositions] = useState({});
  const dropdownRefs = useRef({});
  const questionHeaderRef = useRef(null);
  const comingFromReviewRef = useRef(false);
  // eslint-disable-next-line no-unused-vars
  const [permissions, setPermissions] = useState(null); // Permissions are validated on backend; stored here for potential future UI enhancements

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (Object.keys(openDropdowns).some(key => openDropdowns[key])) {
        const target = event.target;
        if (!target.closest('[data-dropdown]')) {
          setOpenDropdowns({});
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdowns]);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [isAutoSubmitted, setIsAutoSubmitted] = useState(false);
  const [autoSaveFailed, setAutoSaveFailed] = useState(false);
  
  // Network connectivity state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkStatus, setNetworkStatus] = useState('online'); // 'online' | 'offline' | 'unstable'
  const [networkErrorBanner, setNetworkErrorBanner] = useState(null); // { type, message, count }
  const [queuedAnswersCount, setQueuedAnswersCount] = useState(0);
  const connectivityCheckIntervalRef = useRef(null);
  
  // CRITICAL FIX: Cache CSRF token for unload handlers (can't await async in beforeunload)
  const csrfTokenRef = useRef(null);
  const csrfTokenRefreshIntervalRef = useRef(null);


  const formatFillBlankPrompt = (prompt) => {
    if (!prompt) return '';
    // Replace single underscores with 5 underscores for better visual indication
    return prompt.replace(/_{1,}/g, '_____');
  };

  // Handle Arabic text encoding properly
  const handleArabicText = (text) => {
    if (!text) return '';
    
    // If text contains question marks, it might be corrupted Arabic text
    if (text.includes('???') || text.includes('؟؟؟')) {
      logger.warn('Detected potentially corrupted Arabic text', { text: text.substring(0, 100) });
      // Try to decode if it's URL encoded
      try {
        const decoded = decodeURIComponent(encodeURIComponent(text));
        if (decoded !== text) {
          return decoded;
        }
      } catch (e) {
        logger.warn('Failed to decode Arabic text', { error: e });
      }
    }
    
    // Ensure proper Unicode handling for Arabic text
    try {
      // This ensures proper UTF-8 handling
      return decodeURIComponent(encodeURIComponent(text));
    } catch (e) {
      logger.warn('Failed to process Arabic text', { error: e });
      return text;
    }
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return '';
    const date = new Date(dueDate);
    // Format as: "Month Day, Year at Time"
    return date.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };


  // Check refresh token availability before starting quiz
  useEffect(() => {
    if (attemptId && !tokenRefreshManager.hasRefreshToken()) {
      logger.warn('Refresh token not available - user may need to re-authenticate');
      setError('Your session is not properly authenticated. Please refresh the page or log in again.');
    } else if (attemptId && tokenRefreshManager.isRefreshTokenExpiringSoon()) {
      logger.warn('Refresh token expiring soon');
      // Show warning but don't block quiz
      const timeUntilExpiry = tokenRefreshManager.getTimeUntilRefreshTokenExpires();
      const hoursRemaining = Math.floor(timeUntilExpiry / (60 * 60 * 1000));
      if (hoursRemaining <= 1) {
        alert(`Warning: Your session will expire in less than ${hoursRemaining} hour(s). Please save your answers frequently.`);
      }
    }
  }, [attemptId]);

  const fetchAttempt = useCallback(async () => {
    try {
      setLoading(true);
      const response = await quizApiService.getAttempt(attemptId);
      
      if (response.success) {
        // Set attempt with assignment data included
        setAttempt({
          ...response.data.attempt,
          assignment: response.data.assignment
        });
        setQuiz(response.data.quiz);
        
        // FIX: Check if attempt is already submitted - redirect to results immediately
        if (response.data.attempt.submitted_at) {
          logger.info('Attempt already submitted, redirecting to results...', { attemptId });
          // Navigate to results page immediately
          if (response.data.assignment?.immediate_feedback) {
            navigate(`/student/result/${attemptId}`);
          } else {
            navigate('/student/quizzes', {
              state: { message: 'This quiz has already been submitted. Results will be available after the due date.' }
            });
          }
          return;
        }
        
        // CRITICAL FIX: Validate and reset currentQuestionIndex if out of bounds
        // This handles cases where questions array is filtered (e.g., partial retake/edit)
        // Must be done AFTER setQuiz to ensure quiz state is updated
        if (response.data.quiz?.questions) {
          const questionsLength = response.data.quiz.questions.length;
          // Use a function to get the current state value
          setCurrentQuestionIndex(prevIndex => {
            if (prevIndex >= questionsLength) {
              logger.warn('currentQuestionIndex out of bounds on quiz load - likely due to partial retake/edit filtering', { 
                prevIndex, 
                questionsLength,
                attemptId,
                isRetakeMode,
                isEditMode,
                originalQuestionsCount: response.data.quiz?.questions?.length || 'unknown',
                filteredQuestionsCount: questionsLength,
                scenario: 'Student was on question #' + (prevIndex + 1) + ' but quiz was filtered to only ' + questionsLength + ' questions. This typically happens when a partial retake/edit is granted while the student is on a question outside the allowed set.'
              });
              return 0;
            }
            logger.debug('Quiz loaded successfully', { 
              questionsCount: questionsLength,
              currentQuestionIndex: prevIndex,
              attemptId
            });
            return prevIndex;
          });
        }
        
        // Debug: Log the full response data structure (temporarily for debugging)
        // console.log('[Quiz Load] Full response data:', JSON.stringify(response.data, null, 2));
        // console.log('[Quiz Load] Assignment data:', JSON.stringify(response.data.assignment, null, 2));
        // console.log('[Quiz Load] Quiz data:', JSON.stringify(response.data.quiz, null, 2));
        // console.log('[Quiz Load] Attempt data:', JSON.stringify(response.data.attempt, null, 2));
        
        // Initialize responses from existing data
        logger.debug('Loading existing responses', { count: response.data.existing_responses?.length || 0 });
        const existingResponses = {};
        
        // FIX #6: Define escape/unescape functions for pipe character handling
        const PIPE_ESCAPE = '\u001F'; // Unit separator character (rarely used in text)
        const unescapePipe = (text) => {
          if (!text) return text;
          return text.replace(new RegExp(PIPE_ESCAPE, 'g'), '|');
        };
        
        response.data.existing_responses.forEach(resp => {
          logger.debug('Loading response for question', {
            questionId: resp.question_id,
            selected_option_id: resp.selected_option_id,
            answer_text: resp.answer_text ? resp.answer_text.substring(0, 50) : null,
            has_answer_text: !!resp.answer_text
          });
          
          // FIX #6: Improved fill-in-the-blank parsing
          // Check if it's a JSON array first (new format for fill-in-blank with multiple answers)
          if (resp.answer_text && resp.answer_text.startsWith('[') && !resp.answer_text.startsWith('[[')) {
            try {
              const parsed = JSON.parse(resp.answer_text);
              if (Array.isArray(parsed) && parsed.every(item => typeof item === 'string' || typeof item === 'number')) {
                // This is a fill-in-blank with JSON array format
                resp.answer_texts = parsed.map(item => String(item));
                // Don't continue to pipe parsing
                existingResponses[resp.question_id] = resp;
                return;
              }
            } catch (e) {
              // Not valid JSON array, continue to other parsing
            }
          }
          
          // Parse pipe-separated answers for fill-in-the-blank questions
          // FIX #6: Use escape sequence for literal pipes in user input
          if (resp.answer_text && resp.answer_text.includes('|') && !resp.answer_text.startsWith('[')) {
            // Split by pipe, then unescape any escaped pipes in individual answers
            resp.answer_texts = resp.answer_text.split('|').map(unescapePipe);
          } else if (resp.answer_text && !resp.answer_text.startsWith('[')) {
            resp.answer_texts = [unescapePipe(resp.answer_text)];
          }
          
          // Parse JSON array for multiple choice questions with multiple selections
          if (resp.answer_text && resp.answer_text.startsWith('[')) {
            try {
              const parsed = JSON.parse(resp.answer_text);
              if (Array.isArray(parsed)) {
                resp.selected_option_ids = parsed;
              }
            } catch (e) {
              // Not valid JSON, ignore
            }
          } else if (resp.selected_option_id && !resp.answer_text) {
            // Convert old format (only selected_option_id) to new format (answer_text as JSON array)
            // This ensures backward compatibility
            resp.answer_text = JSON.stringify([resp.selected_option_id]);
            resp.selected_option_ids = [resp.selected_option_id];
          }
          
          existingResponses[resp.question_id] = resp;
        });
        logger.debug('Setting responses state', { count: Object.keys(existingResponses).length });
        setResponses(existingResponses);

        // Get time limit from assignment only (no quiz default time limits)
        const timeLimitSeconds = response.data.assignment?.time_limit_minutes 
          ? response.data.assignment.time_limit_minutes * 60 
          : null;
        
        if (timeLimitSeconds) {
          // Get server time to sync timer and account for clock skew
          let serverTime = null;
          try {
            const serverTimeResponse = await quizApiService.getServerTime();
            serverTime = serverTimeResponse;
          } catch (e) {
            logger.warn('Failed to get server time, using client time', { error: e });
          }
          
          const remaining = await quizApiService.calculateTimeRemaining(
            response.data.attempt.started_at,
            timeLimitSeconds,
            serverTime
          );
          
          // MEDIUM ISSUE 17 FIX: Calculate clock skew offset for future timer updates
          // Account for network latency by measuring round-trip time
          if (serverTime) {
            const clientTime = new Date();
            const clockSkew = serverTime.getTime() - clientTime.getTime();
            // Store offset for timer updates
            window.quizClockSkewOffset = clockSkew;
            logger.debug('Clock skew detected', { 
              skew: Math.floor(clockSkew / 1000), 
              serverAhead: clockSkew > 0 
            });
          }
          
          setTimeRemaining(remaining);
        } else {
          setTimeRemaining(null);
          // Enable auto-save for untimed quizzes
          setAutoSaveEnabled(true);

          // For untimed quizzes, record due date for display only; do not auto-submit client-side
          const assignmentDueDate = response.data.assignment?.due_at || response.data.quiz?.due_at;
          if (assignmentDueDate) {
            setDueDate(assignmentDueDate);
          }
        }
      } else {
        setError(response.message || 'Failed to load quiz attempt');
      }
    } catch (err) {
      logger.error('Error fetching attempt', { error: err, attemptId });
      
      // Check if the error is due to attempt already being submitted
      if (err.message && err.message.includes('Attempt already submitted')) {
        setIsAutoSubmitted(true);
        setError('This quiz was automatically submitted when you refreshed the page. Please check your quiz results.');
        // Navigate to results page after a short delay
        setTimeout(() => {
          navigate(`/student/result/${attemptId}`);
        }, 3000);
      } else {
        setError(err.message || 'Failed to load quiz attempt');
      }
    } finally {
      setLoading(false);
    }
  }, [attemptId, navigate, isRetakeMode, isEditMode]);

  const fetchPermissions = useCallback(async () => {
    if (isRetakeMode || isEditMode) {
      try {
        const response = await quizApiService.getAttemptPermissions(attemptId);
        if (response.success && response.data) {
          // Permissions are validated on backend; stored here for potential future UI enhancements
          setPermissions(response.data);
          logger.debug('Retake/Edit permissions loaded', {
            allowRetakeAll: response.data.allowRetakeAll,
            allowEditAll: response.data.allowEditAll,
            retakeQuestionIds: response.data.retakeQuestionIds?.length || 0,
            editQuestionIds: response.data.editQuestionIds?.length || 0
          });
        }
      } catch (err) {
        logger.error('Error fetching permissions', { error: err, attemptId });
      }
    }
  }, [attemptId, isRetakeMode, isEditMode]);

  // Load quiz attempt data on mount
  useEffect(() => {
    if (attemptId) {
      fetchAttempt();
    }
  }, [attemptId, fetchAttempt]);

  // Load permissions if in retake/edit mode
  useEffect(() => {
    if (attemptId && (isRetakeMode || isEditMode)) {
      fetchPermissions();
    }
  }, [attemptId, isRetakeMode, isEditMode, fetchPermissions]);

  // CRITICAL FIX: Validate currentQuestionIndex is within bounds when quiz loads or questions array changes
  // This handles cases where questions array is filtered (e.g., partial retake/edit)
  // This is a safety net in case the fix in fetchAttempt didn't catch it
  // NOTE: Only runs when quiz.questions.length changes, not on every currentQuestionIndex change to prevent flickering
  const questionsLength = quiz?.questions?.length;
  useEffect(() => {
    // Only run if we have questions and a valid length
    if (!quiz?.questions || questionsLength === undefined) {
      return;
    }
    
    // Use a functional update to get the current index value and only update if needed
    setCurrentQuestionIndex(prevIndex => {
      if (prevIndex >= questionsLength) {
        logger.warn('currentQuestionIndex out of bounds - safety net caught it (likely partial retake/edit filtering)', { 
          prevIndex, 
          questionsLength,
          attemptId,
          scenario: 'Student was on question #' + (prevIndex + 1) + ' but quiz was filtered to only ' + questionsLength + ' questions. This typically happens when a partial retake/edit is granted while the student is on a question outside the allowed set.'
        });
        return 0;
      }
      // Return same value - React will skip re-render if value hasn't changed
      return prevIndex;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsLength, attemptId]); // Only depend on length, not the array itself to prevent infinite loops

  // FIX 1: Set mounted status on mount/unmount
  // FIX #2: Comprehensive cleanup of debounce timers on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      
      // FIX #2: Clear all debounce timers on unmount to prevent memory leaks
      // and state updates after unmount
      Object.values(debounceTimersRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
      debounceTimersRef.current = {};
      
      // Clear all saved status timeouts
      Object.values(savedStatusTimeoutsRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
      savedStatusTimeoutsRef.current = {};
      
      // Clear CSRF token refresh interval
      if (csrfTokenRefreshIntervalRef.current) {
        clearInterval(csrfTokenRefreshIntervalRef.current);
        csrfTokenRefreshIntervalRef.current = null;
      }
      
      // Cleanup save manager
      if (saveManagerRef.current) {
        saveManagerRef.current.cleanup();
      }
    };
  }, []);

  // Initialize save manager and offline support
  useEffect(() => {
    // Reset initialization flag when attempt changes (before creating new save manager)
    hasInitializedSaveManagerRef.current = false;
    
    if (attemptId) {
      const saveManager = new QuizSaveManager(attemptId, quizApiService);
      saveManagerRef.current = saveManager;
      
      // CRITICAL FIX: Set state refs immediately to avoid race condition
      // This ensures save manager has access to latest state from the start, preventing
      // auto-save failures when handleResponseChange is called before the separate useEffect
      // that updates refs (line 1705) runs
      saveManager.setStateRefs(responsesRef, setResponsesRef);
      
      // RECOMMENDATION 2: Start periodic sync for failed saves queue
      saveManager.startPeriodicSync();
      
      // Note: Token refresh is now handled automatically by tokenRefreshManager
      // The setTokenRefreshCallback method is kept for backward compatibility but does nothing
      
      // Set status change callback
      saveManager.setStatusChangeCallback((questionId, status, error) => {
        // FIX 1: Check if component is mounted before state update
        if (!isMountedRef.current) {
          return;
        }
        
        // Clear any existing timeout for this question
        if (savedStatusTimeoutsRef.current[questionId]) {
          clearTimeout(savedStatusTimeoutsRef.current[questionId]);
          delete savedStatusTimeoutsRef.current[questionId];
        }
        
        setSaveStatuses(prev => {
          const updated = {
            ...prev,
            [questionId]: {
              status,
              timestamp: Date.now(),
              error: error?.message || error
            }
          };
          
          // Update network error banner based on failed saves
          const failedCount = Object.values(updated).filter(s => s?.status === 'failed').length;
          const queuedCount = saveManager.failedSaveQueue?.length || 0;
          setQueuedAnswersCount(queuedCount);
          
          // Check if any failed saves are due to 503 Service Unavailable
          const hasServiceUnavailable = Object.values(updated).some(s => 
            s?.status === 'failed' && 
            (s?.error?.includes('503') || s?.error?.includes('Service temporarily unavailable'))
          );
          
          // Update banner if there are failed saves
          if (failedCount > 0 && networkStatus === 'online') {
            if (hasServiceUnavailable) {
              setNetworkErrorBanner({
                type: 'service_unavailable',
                severity: 'critical',
                message: `Service temporarily unavailable (503). ${failedCount} answer(s) failed to save. ${queuedCount > 0 ? `${queuedCount} queued for retry.` : ''}`,
                count: failedCount
              });
            } else {
              setNetworkErrorBanner({
                type: 'save_failed',
                severity: 'warning',
                message: `${failedCount} answer(s) failed to save. ${queuedCount > 0 ? `${queuedCount} queued for retry.` : ''}`,
                count: failedCount
              });
            }
          } else if (failedCount === 0 && queuedCount === 0 && networkStatus === 'online') {
            // Clear banner if all saves succeeded
            setNetworkErrorBanner(prev => {
              // Only clear if it's a save-related banner, not network status
              if (prev?.type === 'save_failed' || prev?.type === 'service_unavailable' || prev?.type === 'server_error') {
                return null;
              }
              return prev;
            });
          }
          
          return updated;
        });
        
        // Clear "saved" status after timeout (PRIORITY 2 FIX: Extended timeout for manual saves)
        if (status === 'saved') {
          // Check if this was a manual save (extend timeout to 10 seconds)
          const isManualSave = manualSaveFlagsRef.current[questionId];
          const timeoutDuration = isManualSave ? 10000 : 5000; // 10s for manual, 5s for auto
          
          if (isManualSave) {
            delete manualSaveFlagsRef.current[questionId];
          }
          
          savedStatusTimeoutsRef.current[questionId] = setTimeout(() => {
            // FIX 1 & 4: Check if component is mounted before state update
            if (!isMountedRef.current) {
              delete savedStatusTimeoutsRef.current[questionId];
              return;
            }
            
            setSaveStatuses(prev => {
              const updated = { ...prev };
              // Only clear if status is still "saved" (not changed to something else)
              if (updated[questionId]?.status === 'saved') {
                delete updated[questionId];
              }
              return updated;
            });
            delete savedStatusTimeoutsRef.current[questionId];
          }, timeoutDuration);
        }
      });
      
      // Process any failed saves from previous session
      saveManager.processFailedQueue();
      
      // Firewall detection: Listen for firewall detection events
      const handleFirewallDetected = () => {
        setFirewallWarning({
          message: 'Firewall detected. Requests may take longer than usual. Your answers are being saved automatically.',
          severity: 'info'
        });
      };
      
      const handleFirewallCleared = () => {
        setFirewallWarning(null);
      };
      
      const handleSlowNetwork = () => {
        setFirewallWarning({
          message: 'Slow network detected. Timeout has been increased to ensure your answers are saved.',
          severity: 'info'
        });
      };
      
      // Check initial state
      const checkInitialFirewallState = async () => {
        try {
          const apiService = (await import('../../services/api')).default;
          if (apiService.firewallDetected) {
            handleFirewallDetected({ detail: {} });
          } else if (apiService.slowNetworkDetected) {
            handleSlowNetwork();
          }
        } catch (err) {
          // Silently fail - not critical
        }
      };
      
      // Listen for firewall detection events
      window.addEventListener('firewall-detected', handleFirewallDetected);
      window.addEventListener('firewall-cleared', handleFirewallCleared);
      
      // Check initial state
      checkInitialFirewallState();
      
      // CRITICAL FIX: Pre-fetch and cache CSRF token for unload handlers
      // Unload handlers can't await async operations, so we cache the token synchronously
      (async () => {
        const apiService = (await import('../../services/api')).default;
        try {
          const token = await apiService.fetchCSRFToken();
          if (token) {
            csrfTokenRef.current = token;
            logger.debug('CSRF token cached for unload handlers');
          }
        } catch (err) {
          logger.warn('Failed to pre-fetch CSRF token', { error: err });
        }
        
        // Periodically refresh CSRF token cache (every 30 minutes)
        csrfTokenRefreshIntervalRef.current = setInterval(async () => {
          try {
            const token = await apiService.fetchCSRFToken();
            if (token) {
              csrfTokenRef.current = token;
            }
          } catch (err) {
            logger.warn('Failed to refresh CSRF token cache', { error: err });
          }
        }, 30 * 60 * 1000); // 30 minutes
      })();
      
      // Basic offline support: cache quiz data in localStorage
      const cacheKey = `quiz_cache_${attemptId}`;
      const cacheQuizData = () => {
        try {
          const quizData = {
            quiz,
            attempt,
            responses,
            timestamp: Date.now()
          };
          localStorage.setItem(cacheKey, JSON.stringify(quizData));
        } catch (e) {
          logger.warn('Failed to cache quiz data', { error: e });
        }
      };
      
      // Cache quiz data when it changes
      if (quiz && attempt) {
        cacheQuizData();
      }
      
      // Network connectivity detection and monitoring
      const checkConnectivity = async () => {
        const wasOnline = isOnline;
        const browserOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
        
        // Check actual connectivity by pinging server
        let serverReachable = false;
        let serverStatus = null; // Track specific server status (503, 500, etc.)
        if (browserOnline) {
          try {
            // IMPROVED: Increased timeout to 8 seconds for firewall environments
            // Try HEAD first (lightweight), fallback to GET if HEAD fails
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 8000); // Increased from 3s to 8s
            
            let response = null;
            let headFailed = false;
            
            // Try HEAD request first (lightweight)
            try {
              response = await fetch('/portal/api/config', {
                method: 'HEAD',
                signal: controller.signal,
                cache: 'no-cache'
              });
              
              // Check if response is 404 - endpoint might not exist, try GET as fallback
              if (response.status === 404) {
                headFailed = true;
                clearTimeout(timeoutId);
                const getController = new AbortController();
                const getTimeoutId = setTimeout(() => getController.abort(), 8000);
                try {
                  response = await fetch('/portal/api/config', {
                    method: 'GET',
                    signal: getController.signal,
                    cache: 'no-cache'
                  });
                  clearTimeout(getTimeoutId);
                } catch (getErr) {
                  clearTimeout(getTimeoutId);
                  if (getErr.name !== 'AbortError') {
                    throw getErr;
                  }
                  response = null;
                }
              }
            } catch (headErr) {
              // HEAD might be blocked by firewall or network error - try GET as fallback
              headFailed = true;
              // Only log if it's not an abort (timeout)
              if (headErr.name !== 'AbortError') {
                logger.debug('HEAD request failed, trying GET fallback', { error: headErr });
              }
              
              // Clear existing timeout and create new one for GET
              clearTimeout(timeoutId);
              const getController = new AbortController();
              const getTimeoutId = setTimeout(() => getController.abort(), 8000);
              
              try {
                response = await fetch('/portal/api/config', {
                  method: 'GET',
                  signal: getController.signal,
                  cache: 'no-cache'
                });
                clearTimeout(getTimeoutId);
              } catch (getErr) {
                clearTimeout(getTimeoutId);
                // Only throw if it's not an abort
                if (getErr.name !== 'AbortError') {
                  throw getErr;
                }
                response = null;
              }
            }
            
            if (!headFailed) {
              clearTimeout(timeoutId);
            }
            
            // FIX #8: Improved response status handling to reduce false positives
            // Only check response status if we have a valid response
            if (response) {
              serverStatus = response.status;
              // Server is reachable if: OK (200), needs auth (401), or other non-5xx errors
              // FIX #8: 404 means endpoint doesn't exist - treat as REACHABLE (server responded)
              // This prevents false offline warnings when the config endpoint isn't implemented
              // 503 Service Unavailable means server is overloaded/down - treat as unreachable
              if (response.status === 404) {
                // FIX #8: Endpoint doesn't exist but server responded - treat as reachable
                // The server is working, just this specific endpoint isn't available
                serverReachable = true;
                serverStatus = 404; // Keep status for logging but don't treat as error
                logger.debug('Config endpoint not found (404) but server is reachable');
              } else {
                serverReachable = response.ok || response.status === 401 || (response.status < 500 && response.status !== 503);
              }
            } else {
              serverReachable = false;
              serverStatus = null;
            }
          } catch (err) {
            serverReachable = false;
            serverStatus = null;
            // Log connectivity check failure for debugging
            if (err.name !== 'AbortError') {
              logger.warn('Connectivity check failed', { error: err });
            }
          }
        }
        
        const nowOnline = browserOnline && serverReachable;
        setIsOnline(nowOnline);
        
        // Determine network status
        if (!browserOnline) {
          setNetworkStatus('offline');
          setNetworkErrorBanner({
            type: 'offline',
            severity: 'critical',
            message: 'You are offline. Answers will be saved when your connection returns.',
            count: queuedAnswersCount
          });
        } else if (!serverReachable) {
          // Only mark as unstable if we have a specific error status or multiple failures
          // Don't mark as unstable for transient timeouts or single failures
          // Check if it's a 503 Service Unavailable error
          if (serverStatus === 503) {
            setNetworkStatus('unstable');
            setNetworkErrorBanner({
              type: 'service_unavailable',
              severity: 'critical',
              message: 'Service temporarily unavailable (503). The server is overloaded or down. Your answers are being queued and will be saved when the service is available.',
              count: queuedAnswersCount
            });
          } else if (serverStatus === 500) {
            setNetworkStatus('unstable');
            setNetworkErrorBanner({
              type: 'server_error',
              severity: 'critical',
              message: 'Server error (500). Please try again in a moment. Your answers are being queued.',
              count: queuedAnswersCount
            });
          } else {
            // For timeouts or network errors, only mark as unstable if we have failed saves
            // This prevents false positives from transient network issues
            const hasFailedSaves = Object.values(saveStatuses).some(s => s?.status === 'failed');
            if (hasFailedSaves || queuedAnswersCount > 0) {
              setNetworkStatus('unstable');
              setNetworkErrorBanner({
                type: 'unstable',
                severity: 'warning',
                message: 'Connection unstable. Some answers may not save properly.',
                count: queuedAnswersCount
              });
            } else {
              // Transient network issue without failed saves - don't show warning
              // Keep status as 'online' but log for debugging
              logger.debug('Connectivity check failed but no failed saves, keeping status as online', {
                serverStatus,
                browserOnline
              });
            }
          }
        } else {
          setNetworkStatus('online');
          // Clear banner if we were offline/unstable and now online
          if (!wasOnline) {
            setNetworkErrorBanner({
              type: 'restored',
              severity: 'info',
              message: 'Connection restored. Saving queued answers...',
              count: 0
            });
            // Auto-dismiss success message after 5 seconds
            setTimeout(() => {
              setNetworkErrorBanner(null);
            }, 5000);
          } else {
            // Only clear if no other errors
            const hasFailedSaves = Object.values(saveStatuses).some(s => s?.status === 'failed');
            if (!hasFailedSaves) {
              setNetworkErrorBanner(null);
            }
          }
        }
      };
      
      // Initial connectivity check
      checkConnectivity();
      
      // Periodic connectivity check (every 30 seconds)
      connectivityCheckIntervalRef.current = setInterval(checkConnectivity, 30000);
      
      // Listen for online/offline events
      const handleOnline = () => {
        logger.debug('Network online event detected');
        checkConnectivity();
        saveManager.processFailedQueue();
      };
      
      const handleOffline = () => {
        logger.debug('Network offline event detected');
        setIsOnline(false);
        setNetworkStatus('offline');
        setNetworkErrorBanner({
          type: 'offline',
          severity: 'critical',
          message: 'You are offline. Answers will be saved when your connection returns.',
          count: queuedAnswersCount
        });
      };
      
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      
      // Process any failed saves from previous session
      saveManager.processFailedQueue();
      
      // Cleanup function
      return () => {
        // Clean up debounce timers FIRST to prevent new saves during cleanup
        if (debounceTimersRef.current) {
          Object.values(debounceTimersRef.current).forEach(timer => {
            if (timer) clearTimeout(timer);
          });
          debounceTimersRef.current = {};
        }
        
        // Clean up saved status timeouts
        if (savedStatusTimeoutsRef.current) {
          Object.values(savedStatusTimeoutsRef.current).forEach(timer => {
            if (timer) clearTimeout(timer);
          });
          savedStatusTimeoutsRef.current = {};
        }
        
        // Clean up manual save flags
        manualSaveFlagsRef.current = {};
        
        // FIX 2: Clean up periodic save timeout
        if (periodicSaveTimeoutRef.current) {
          clearTimeout(periodicSaveTimeoutRef.current);
          periodicSaveTimeoutRef.current = null;
        }
        
        // FIX 3: Clean up timed quiz interval
        if (timedSaveIntervalRef.current) {
          clearInterval(timedSaveIntervalRef.current);
          timedSaveIntervalRef.current = null;
        }
        
        // Clean up connectivity check interval
        if (connectivityCheckIntervalRef.current) {
          clearInterval(connectivityCheckIntervalRef.current);
          connectivityCheckIntervalRef.current = null;
        }
        
        // Clean up CSRF token refresh interval
        if (csrfTokenRefreshIntervalRef.current) {
          clearInterval(csrfTokenRefreshIntervalRef.current);
          csrfTokenRefreshIntervalRef.current = null;
        }
        
        // Clean up firewall detection event listeners
        window.removeEventListener('firewall-detected', handleFirewallDetected);
        window.removeEventListener('firewall-cleared', handleFirewallCleared);
        
        // Then cleanup save manager
        if (saveManager) {
          saveManager.cleanup();
        }
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, [attemptId, quiz, attempt, responses, isOnline, networkStatus, queuedAnswersCount, saveStatuses]); // responses included to initialize save manager when responses load
  
  // Update refs when responses change (fixes stale closure issues)
  // Note: State refs are also set during save manager initialization (line 1637),
  // but this ensures they stay updated as responses change
  useEffect(() => {
    responsesRef.current = responses;
    setResponsesRef.current = setResponses;
    
    // Update save manager refs (in case save manager was created before this effect ran)
    // This is a safety measure - refs are already set during initialization
    if (saveManagerRef.current) {
      saveManagerRef.current.setStateRefs(responsesRef, setResponsesRef);
    }
  }, [responses]);
  
  // Initialize save manager with existing responses when they're first loaded
  // This ensures questions loaded from database show as "saved" (green bordered)
  useEffect(() => {
    // Only initialize once when responses are first loaded and save manager exists
    if (!saveManagerRef.current || !responses || Object.keys(responses).length === 0) {
      return;
    }
    
    // Check if we've already initialized (to avoid re-initializing on every response change)
    if (hasInitializedSaveManagerRef.current) {
      return;
    }
    
    // Mark that we've initialized
    hasInitializedSaveManagerRef.current = true;
    
    logger.debug('Save Manager initializing', { responseCount: Object.keys(responses).length });
    Object.keys(responses).forEach(questionId => {
      const response = responses[questionId];
      if (response) {
        // Mark as saved in save manager's internal state
        saveManagerRef.current.markResponseSaved(questionId, response);
        
        // Set save status to "saved" (will show green border)
        // Note: Status will clear after 5 seconds (by design), but initial load shows "saved"
        if (isMountedRef.current) {
          setSaveStatuses(prev => ({
            ...prev,
            [questionId]: {
              status: 'saved',
              timestamp: Date.now(),
              error: null
            }
          }));
        }
      }
    });
    logger.debug('Save Manager initialized save status for existing responses');
  }, [responses]); // Only depends on responses - runs when responses are first loaded

  // No client-side auto-submit for untimed quizzes; nothing to clean up here

  // Auto-save for untimed quizzes using save manager (debounced, only saves changed questions)
  // FIX 2: Use ref to track timeout and prevent multiple timeouts
  useEffect(() => {
    if (!autoSaveEnabled || !saveManagerRef.current) {
      return;
    }
    
    // FIX 2: Clear existing timeout before creating new one
    if (periodicSaveTimeoutRef.current) {
      clearTimeout(periodicSaveTimeoutRef.current);
      periodicSaveTimeoutRef.current = null;
    }
    
    const latestResponses = responsesRef.current;
    if (!latestResponses || Object.keys(latestResponses).length === 0) {
      return;
    }
    
    periodicSaveTimeoutRef.current = setTimeout(async () => {
      // FIX 1: Check if component is mounted
      if (!isMountedRef.current) {
        periodicSaveTimeoutRef.current = null;
        return;
      }
      
      // Double-check conditions before saving
      if (!saveManagerRef.current) {
        logger.warn('Save manager not available during periodic save');
        periodicSaveTimeoutRef.current = null;
        return;
      }
      
      // FIX 7: Use both state and ref for current responses
      const currentResponses = responsesRef.current || responses;
      if (!currentResponses || Object.keys(currentResponses).length === 0) {
        periodicSaveTimeoutRef.current = null;
        return;
      }
      
      // CRITICAL FIX: Filter out responses without valid answers before saving
      // Only save responses that have actual answers selected/entered
      const validResponses = {};
      if (quiz?.questions) {
        Object.keys(currentResponses).forEach(qId => {
          const question = quiz.questions.find(q => q.id === qId);
          const response = currentResponses[qId];
          if (question && response && hasValidAnswer(question.type, response)) {
            validResponses[qId] = response;
          }
        });
      }
      
      if (Object.keys(validResponses).length === 0) {
        logger.debug('No valid responses to save in periodic auto-save');
        periodicSaveTimeoutRef.current = null;
        return;
      }
      
      logger.debug('Starting periodic auto-save', { 
        totalResponses: Object.keys(currentResponses).length,
        validResponses: Object.keys(validResponses).length 
      });
      
      try {
        const result = await saveManagerRef.current.saveAllResponses(validResponses, {
          skipUnchanged: true, // Only save changed questions
          skipVerification: true, // Skip verification for auto-save (faster)
          onProgress: (questionId, success) => {
            // FIX 1: Check if component is mounted before state update
            if (!isMountedRef.current) return;
            
            if (!success) {
              logger.warn('Failed to save question during auto-save', { questionId });
              // Update save status
              setSaveStatuses(prev => ({
                ...prev,
                [questionId]: {
                  status: 'failed',
                  timestamp: Date.now(),
                  error: 'Periodic save failed'
                }
              }));
            }
          }
        });
        
        // FIX 1: Check if component is mounted before state update
        if (!isMountedRef.current) {
          periodicSaveTimeoutRef.current = null;
          return;
        }
        
        if (result.success) {
          logger.debug('Auto-save completed', { saved: result.saved, skipped: result.skipped });
          // Reset auto-save failed state on success
          if (autoSaveFailed) {
            setAutoSaveFailed(false);
          }
        } else if (result.failed > 0) {
          logger.debug('Auto-save completed', { 
            saved: result.saved, 
            skipped: result.skipped, 
            failed: result.failed 
          });
          setAutoSaveFailed(true);
          // Don't show alert for auto-save failures (too intrusive)
          // User can see status indicators and retry manually
        }
        
        periodicSaveTimeoutRef.current = null;
      } catch (err) {
        // FIX 1: Check if component is mounted before state update
        if (isMountedRef.current) {
          logger.error('Error during periodic save', { error: err });
          setAutoSaveFailed(true);
        }
        periodicSaveTimeoutRef.current = null;
      }
    }, 2000); // Auto-save after 2 seconds of inactivity

    return () => {
      // FIX 2: Clear timeout on cleanup
      if (periodicSaveTimeoutRef.current) {
        clearTimeout(periodicSaveTimeoutRef.current);
        periodicSaveTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses, autoSaveEnabled, autoSaveFailed, quiz, hasValidAnswer]);

  // Auto-save for timed quizzes - periodic saves every 30 seconds (backup mechanism)
  // Note: Immediate saves happen in handleResponseChange, this is just a backup
  // FIX 3: Use ref to track interval and prevent multiple intervals
  useEffect(() => {
    const isTimedQuiz = timeRemaining !== null && timeRemaining > 0;
    const hasTimeLimit = attempt?.assignment?.time_limit_minutes && attempt.assignment.time_limit_minutes > 0;
    
    if (!isTimedQuiz || !hasTimeLimit || !saveManagerRef.current) {
      // FIX 3: Clear interval if conditions not met
      if (timedSaveIntervalRef.current) {
        clearInterval(timedSaveIntervalRef.current);
        timedSaveIntervalRef.current = null;
      }
      return;
    }
    
    // FIX 3: Clear existing interval before creating new one
    if (timedSaveIntervalRef.current) {
      clearInterval(timedSaveIntervalRef.current);
      timedSaveIntervalRef.current = null;
    }
    
    const latestResponses = responsesRef.current;
    if (!latestResponses || Object.keys(latestResponses).length === 0) {
      return;
    }
    
    timedSaveIntervalRef.current = setInterval(() => {
      // FIX 1: Check if component is mounted
      if (!isMountedRef.current) {
        return;
      }
      
      // Double-check conditions before saving
      if (!saveManagerRef.current) {
        logger.warn('Save manager not available during timed quiz periodic save');
        return;
      }
      
      // FIX 7: Use ref only (updated by separate useEffect when responses changes)
      const currentResponses = responsesRef.current;
      if (!currentResponses || Object.keys(currentResponses).length === 0) {
        return;
      }
      
      // CRITICAL FIX: Filter out responses without valid answers before saving
      // Only save responses that have actual answers selected/entered
      const validResponses = {};
      if (quiz?.questions) {
        Object.keys(currentResponses).forEach(qId => {
          const question = quiz.questions.find(q => q.id === qId);
          const response = currentResponses[qId];
          if (question && response && hasValidAnswer(question.type, response)) {
            validResponses[qId] = response;
          }
        });
      }
      
      if (Object.keys(validResponses).length === 0) {
        logger.debug('No valid responses to save in timed quiz periodic save');
        return;
      }
      
      logger.debug('Starting periodic backup save for timed quiz', { 
        totalResponses: Object.keys(currentResponses).length,
        validResponses: Object.keys(validResponses).length 
      });
      
      saveManagerRef.current.saveAllResponses(validResponses, {
        skipUnchanged: true, // Only save changed questions
        skipVerification: true, // Faster for periodic saves
        onProgress: (questionId, success) => {
          // FIX 1: Check if component is mounted before state update
          if (!isMountedRef.current) return;
          
          if (!success) {
            logger.warn('Failed to save question during timed quiz auto-save', { questionId });
            // Update save status
            setSaveStatuses(prev => ({
              ...prev,
              [questionId]: {
                status: 'failed',
                timestamp: Date.now(),
                error: 'Periodic save failed'
              }
            }));
          }
        }
      }).then(result => {
        // FIX 1: Check if component is mounted before logging
        if (!isMountedRef.current) return;
        
        if (result.success) {
          logger.debug('Timed quiz auto-save completed', { saved: result.saved, skipped: result.skipped });
        } else if (result.failed > 0) {
          logger.error('Timed quiz auto-save failed for some responses', { failed: result.failed });
        }
      }).catch(err => {
        // FIX 1: Check if component is mounted before logging
        if (isMountedRef.current) {
          logger.error('Error during timed quiz periodic save', { error: err });
        }
      });
    }, 30000); // Every 30 seconds (backup mechanism)

    return () => {
      // FIX 3: Clear interval on cleanup
      if (timedSaveIntervalRef.current) {
        clearInterval(timedSaveIntervalRef.current);
        timedSaveIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRemaining, attempt, attemptId]); // FIX 3: responses not needed - responsesRef is updated by separate useEffect when responses changes

  const getBlankCount = (prompt) => {
    if (!prompt) return 0;
    const matches = prompt.match(/_{1,}/g);
    return matches ? matches.length : 0;
  };

  const handleResponseChange = (questionId, value, type, blankIndex = null) => {
    // UI FIX 3: Disable interactions during auto-submit
    if (isAutoSubmitting) {
      logger.warn('Cannot change answer during auto-submit');
      return;
    }

    // DEBUG: Log response change for save issue analysis
    logger.debug('Response change triggered', {
      questionId,
      type,
      value: type === 'fill_blank' ? '[REDACTED]' : value,
      blankIndex,
      timestamp: Date.now()
    });
    // CRITICAL FIX: Read from ref first (always current), fallback to state
    // This prevents race conditions when user rapidly changes answers
    const currentResponse = responsesRef.current?.[questionId] || responses[questionId];
    // Initialize newResponse with proper structure, ensuring all fields are defined
    const newResponse = currentResponse ? { ...currentResponse } : {
      selected_option_id: null,
      selected_option_ids: null,
      answer_text: null,
      answer_texts: null
    };
    
    if (type === 'multiple_choice') {
      // FIX #4: Standardized multiple choice answer handling
      // Primary source of truth: selected_option_ids array
      // Other fields are derived for backward compatibility
      let selectedOptionIds = [];
      
      // FIX #4: Read from selected_option_ids first (primary source)
      if (newResponse.selected_option_ids && Array.isArray(newResponse.selected_option_ids)) {
        selectedOptionIds = [...newResponse.selected_option_ids];
      } else if (newResponse.answer_text) {
        // Fallback: Try to parse from answer_text (for backward compatibility)
        try {
          const parsed = JSON.parse(newResponse.answer_text);
          if (Array.isArray(parsed)) {
            selectedOptionIds = parsed;
          }
        } catch (e) {
          // Not JSON, might be old format
        }
      }
      
      // Fallback to selected_option_id (legacy single selection)
      if (selectedOptionIds.length === 0 && newResponse.selected_option_id) {
        selectedOptionIds = [newResponse.selected_option_id];
      }
      
      // Toggle the option
      const optionIndex = selectedOptionIds.indexOf(value);
      if (optionIndex > -1) {
        // Remove if already selected
        selectedOptionIds = selectedOptionIds.filter(id => id !== value);
      } else {
        // Add if not selected
        selectedOptionIds = [...selectedOptionIds, value];
      }
      
      // FIX #4: selected_option_ids is the primary source of truth
      newResponse.selected_option_ids = selectedOptionIds;
      
      // FIX #4: Derive other fields from selected_option_ids for backward compatibility
      // answer_text: JSON array for API/storage
      newResponse.answer_text = selectedOptionIds.length > 0 ? JSON.stringify(selectedOptionIds) : null;
      // selected_option_id: First selected option for legacy API compatibility
      newResponse.selected_option_id = selectedOptionIds.length > 0 ? selectedOptionIds[0] : null;
      newResponse.answer_texts = null;
    } else if (type === 'true_false') {
      newResponse.selected_option_id = value;
      newResponse.selected_option_ids = null;
      newResponse.answer_text = null;
      newResponse.answer_texts = null;
    } else if (type === 'fill_blank') {
      // FIX #6: Escape pipe characters in user input to prevent parsing issues
      const PIPE_ESCAPE = '\u001F'; // Unit separator character
      const escapePipe = (text) => {
        if (!text) return text;
        return text.replace(/\|/g, PIPE_ESCAPE);
      };
      
      if (blankIndex !== null) {
        // Multiple blanks - update specific blank
        // CRITICAL FIX: Ensure answer_texts is properly initialized as an array
        const answerTexts = Array.isArray(newResponse.answer_texts)
          ? [...newResponse.answer_texts]
          : [];
        // Ensure array is large enough for the blankIndex
        while (answerTexts.length <= blankIndex) {
          answerTexts.push('');
        }
        answerTexts[blankIndex] = value;
        newResponse.answer_texts = answerTexts;
        // FIX #6: Escape pipes in individual answers before joining
        newResponse.answer_text = answerTexts.map(escapePipe).join('|'); // Pipe-separated for backend
      } else {
        // Single blank - use original logic
        // FIX #6: Escape pipes in single answer
        newResponse.answer_text = escapePipe(value || '');
        newResponse.answer_texts = [value || ''];
      }
      newResponse.selected_option_id = null;
      newResponse.selected_option_ids = null;
    } else if (type === 'matching') {
      // value should be { leftIndex, rightIndex } or null to remove a pair
      // CRITICAL FIX: Ensure pairs array is properly initialized
      let pairs = [];
      try {
        if (newResponse.answer_text && typeof newResponse.answer_text === 'string') {
          const parsed = JSON.parse(newResponse.answer_text);
          if (Array.isArray(parsed)) {
            pairs = parsed;
          }
        }
      } catch (e) {
        // Invalid JSON or not an array - start with empty array
        pairs = [];
      }
      
      if (value === null) {
        // Remove pair for leftIndex
        const leftIndex = blankIndex; // Using blankIndex to pass leftIndex
        pairs = pairs.filter(p => p.leftIndex !== leftIndex);
      } else {
        // Add or update pair
        // CRITICAL FIX: Validate value structure before destructuring
        if (value && typeof value === 'object' && 'leftIndex' in value) {
          const { leftIndex, rightIndex } = value;
          pairs = pairs.filter(p => p.leftIndex !== leftIndex);
          if (rightIndex !== null && rightIndex !== undefined) {
            pairs.push({ leftIndex, rightIndex });
          }
        }
      }
      
      newResponse.answer_text = JSON.stringify(pairs);
      newResponse.selected_option_id = null;
      newResponse.selected_option_ids = null;
      newResponse.answer_texts = null;
    }
    
    // FIX #1: Improved state update to prevent race conditions
    // Update ref BEFORE state to ensure it's always current
    // This prevents race conditions when multiple rapid answer changes occur
    const updatedResponses = { ...responsesRef.current, [questionId]: newResponse };
    responsesRef.current = updatedResponses;
    
    // Then update state (will trigger re-render)
    setResponses(updatedResponses);
    
    // IMMEDIATE AUTO-SAVE: Save immediately when answer changes (for both timed and untimed quizzes)
    // CRITICAL FIX: Add comprehensive null checks and better error handling
    if (!saveManagerRef.current) {
      logger.warn('Save manager not initialized for question. Save will be queued.', { questionId });
      // Queue the save attempt - it will be processed when save manager is ready
      // The periodic auto-save will pick it up
      return;
    }

    // CRITICAL FIX: Get question type and validate that answer is actually selected/entered
    // Only save if there's a valid answer - don't save empty responses
    const question = quiz?.questions?.find(q => q.id === questionId);

    // DEBUG: Log auto-save initiation
    logger.debug('Auto-save initiated for question', {
      questionId,
      type,
      hasValidAnswer: hasValidAnswer(question?.type, newResponse),
      timestamp: Date.now()
    });
    if (!question) {
      logger.warn('Question not found for validation', { questionId });
      return;
    }
    
    // Use question-type-specific validation to ensure answer is actually provided
    if (!hasValidAnswer(question.type, newResponse)) {
      logger.debug('Skipping save for question: No valid answer selected/entered', { 
        questionId, 
        questionType: question.type,
        response: newResponse 
      });
      return;
    }
    
    // Clear any existing debounce timer for this question
    const existingTimer = debounceTimersRef.current[questionId];
    if (existingTimer) {
      clearTimeout(existingTimer);
      // Timer was cleared, but keep 'saving' status since we're setting a new one
    }
    
    // CRITICAL FIX: Set save status to 'saving' IMMEDIATELY when timer is set
    // This ensures hasUnsavedAnswers detects it right away (fixes race condition)
    // This is especially important for the last question when user clicks "Review Answers"
    // FIX: Only set status if not already saving to prevent conflicts with save manager
    const timerStartTime = Date.now();
    if (isMountedRef.current) {
      setSaveStatuses(prev => {
        const currentStatus = prev[questionId];
        // Don't override if already saving (prevents conflicts with manual saves)
        if (currentStatus?.status === 'saving') {
          return prev;
        }
        return {
          ...prev,
          [questionId]: {
            status: 'saving',
            timestamp: timerStartTime,
            error: null
          }
        };
      });
    }

    // Debounce immediate save to avoid too many requests (500ms delay)
    debounceTimersRef.current[questionId] = setTimeout(() => {
      // Use the latest response from ref to avoid stale closures
      const latestResponse = responsesRef.current?.[questionId] || newResponse;
      
      // Double-check response validity before saving using question type
      // CRITICAL FIX: Get question type and validate that answer is actually selected/entered
      const latestQuestion = quiz?.questions?.find(q => q.id === questionId);
      if (!latestQuestion) {
        logger.warn('Question not found for validation in debounced save', { questionId });
        delete debounceTimersRef.current[questionId];
        if (isMountedRef.current) {
          setSaveStatuses(prev => {
            const updated = { ...prev };
            if (updated[questionId]?.status === 'saving') {
              delete updated[questionId];
            }
            return updated;
          });
        }
        return;
      }
      
      // Use question-type-specific validation to ensure answer is actually provided
      if (!hasValidAnswer(latestQuestion.type, latestResponse)) {
        logger.debug('Skipping save for question: Response became invalid or no answer selected/entered', {
          questionId,
          questionType: latestQuestion.type,
          response: latestResponse
        });
        delete debounceTimersRef.current[questionId];
        // Clear 'saving' status since we're not saving
        // FIX: Only clear if this timer is still the active one (not overridden by newer timer)
        if (isMountedRef.current) {
          setSaveStatuses(prev => {
            const updated = { ...prev };
            const currentStatus = updated[questionId];
            // Only clear if status is 'saving' and timestamp matches our timer start
            if (currentStatus?.status === 'saving' && currentStatus?.timestamp === timerStartTime) {
              delete updated[questionId];
            }
            return updated;
          });
        }
        return;
      }
      
      // Ensure save manager is still available
      if (!saveManagerRef.current) {
        logger.warn('Save manager became unavailable for question', { questionId });
        delete debounceTimersRef.current[questionId];
        // Clear 'saving' status since we can't save
        if (isMountedRef.current) {
          setSaveStatuses(prev => {
            const updated = { ...prev };
            if (updated[questionId]?.status === 'saving') {
              updated[questionId] = {
                status: 'failed',
                timestamp: Date.now(),
                error: 'Save manager unavailable'
              };
            }
            return updated;
          });
        }
        return;
      }

      // FIX: Check if manual save is already in progress to prevent duplicate saves
      if (manualSaveFlagsRef.current[questionId]) {
        logger.debug('Skipping auto-save: manual save in progress', { questionId });
        delete debounceTimersRef.current[questionId];
        return;
      }
      
      // FIX: Check component mount state before saving
      if (!isMountedRef.current) {
        logger.debug('Skipping auto-save: component unmounted', { questionId });
        delete debounceTimersRef.current[questionId];
        return;
      }

      logger.debug('Saving question immediately after answer change', {
        questionId,
        hasResponse: !!latestResponse,
        timestamp: Date.now()
      });

      saveManagerRef.current.saveResponse(questionId, latestResponse, {
        skipVerification: true, // Faster for auto-save
        maxRetries: 2
      }).then(result => {
        // FIX 1: Check if component is mounted before state update
        if (!isMountedRef.current) return;
        
        if (result.success) {
          logger.debug('Successfully saved question', { questionId });
          // FIX 8: Reset auto-save failed state on immediate save success
          if (autoSaveFailed) {
            setAutoSaveFailed(false);
          }
        } else if (result.cancelled) {
          logger.debug('Save cancelled for question (newer save in progress)', { questionId });
        } else {
          logger.warn('Failed to save question', { questionId, error: result.error });
          // Update save status to show failure
          setSaveStatuses(prev => ({
            ...prev,
            [questionId]: {
              status: 'failed',
              timestamp: Date.now(),
              error: result.error?.message || 'Save failed'
            }
          }));
        }
      }).catch(err => {
        // FIX 1: Check if component is mounted before state update
        if (!isMountedRef.current) return;
        
        logger.error('Error saving question', { questionId, error: err });
        // Update save status to show failure
        setSaveStatuses(prev => ({
          ...prev,
          [questionId]: {
            status: 'failed',
            timestamp: Date.now(),
            error: err?.message || 'Save failed'
          }
        }));
      });
      
      delete debounceTimersRef.current[questionId];
    }, 500); // 500ms debounce for immediate saves
  };

  // Save current question before navigating away
  // CRITICAL: Must be defined before handleSubmit to avoid initialization error
  const saveCurrentQuestionBeforeNavigation = useCallback(async () => {
    if (!saveManagerRef.current || !quiz?.questions || currentQuestionIndex < 0) {
      return;
    }
    
    const currentQuestion = quiz.questions[currentQuestionIndex];
    if (!currentQuestion) {
      return;
    }
    
    // PRIORITY 1 FIX: Use both state and ref, prefer state (more current)
    // This ensures we get the latest answer even if user just changed it
    const currentResponse = responses[currentQuestion.id] || responsesRef.current?.[currentQuestion.id];
    if (!currentResponse) {
      return;
    }
    
    // CRITICAL FIX: Use question-type-specific validation to ensure answer is actually selected/entered
    // Only save if there's a valid answer - don't save empty responses
    if (!hasValidAnswer(currentQuestion.type, currentResponse)) {
      logger.debug('Skipping save before navigation: No valid answer selected/entered', { 
        questionId: currentQuestion.id,
        questionType: currentQuestion.type,
        response: currentResponse 
      });
      return;
    }
    
    // FIX 5: Clear all pending debounce timers before navigation save
    Object.keys(debounceTimersRef.current).forEach(questionId => {
      const timer = debounceTimersRef.current[questionId];
      if (timer) {
        clearTimeout(timer);
        delete debounceTimersRef.current[questionId];
      }
    });
    logger.debug('Cleared all pending debounced saves before navigation');
    
    // Check if response has changed (don't save if already saved)
    // Note: hasResponseChanged is a method, so we need to call it correctly
    try {
      if (saveManagerRef.current && typeof saveManagerRef.current.hasResponseChanged === 'function') {
        if (!saveManagerRef.current.hasResponseChanged(currentQuestion.id, currentResponse)) {
          logger.debug('Question already saved, skipping', { questionId: currentQuestion.id });
          return;
        }
      }
    } catch (err) {
      // If check fails, proceed with save (better safe than sorry)
      logger.warn('Could not check if response changed', { error: err });
    }
    
    // FIX 9: Save immediately and wait for completion (with timeout to prevent blocking)
    try {
      // Set a timeout to prevent blocking navigation too long
      const savePromise = saveManagerRef.current.saveResponse(currentQuestion.id, currentResponse, {
        skipVerification: true,
        maxRetries: 1  // Quick retry, don't wait too long
      });
      
      // Race between save and timeout (max 3 seconds)
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve({ timeout: true }), 3000);
      });
      
      const result = await Promise.race([savePromise, timeoutPromise]);
      
      if (result.timeout) {
        logger.warn('Save timed out for question, proceeding with navigation', { questionId: currentQuestion.id });
        // Don't block navigation - the periodic save will catch it
      } else if (result.success) {
        logger.debug('Successfully saved question before navigation', { questionId: currentQuestion.id });
      } else if (result.cancelled) {
        logger.debug('Save cancelled for question (newer save in progress)', { questionId: currentQuestion.id });
      } else {
        logger.warn('Failed to save question before navigation', { questionId: currentQuestion.id, error: result.error });
      }
    } catch (err) {
      logger.warn('Error saving question before navigation', { questionId: currentQuestion.id, error: err });
      // Don't block navigation - the periodic save will catch it
    }
  }, [currentQuestionIndex, quiz, responses, hasValidAnswer]); // PRIORITY 3 FIX: Add responses to dependencies to ensure latest data

  const saveResponse = async (questionId) => {
    // DEBUG: Log manual save attempt
    logger.debug('Manual save initiated', {
      questionId,
      hasResponse: !!(responses[questionId] || responsesRef.current[questionId]),
      hasSaveManager: !!saveManagerRef.current,
      timestamp: Date.now()
    });

    // PRIORITY 1 FIX: Cancel any pending auto-save for this question
    if (debounceTimersRef.current[questionId]) {
      clearTimeout(debounceTimersRef.current[questionId]);
      delete debounceTimersRef.current[questionId];
      logger.debug('Cancelled pending auto-save for question', { questionId });
    }

    // PRIORITY 4 FIX: Use both state and ref, prefer state (more current)
    const response = responses[questionId] || responsesRef.current[questionId];
    if (!response || !saveManagerRef.current) {
      logger.warn('Save aborted: missing response or save manager', {
        questionId,
        hasResponse: !!response,
        hasSaveManager: !!saveManagerRef.current
      });
      return;
    }

    try {
      setSaving(true);
      
      // PRIORITY 2 FIX: Track this as a manual save for extended status timeout
      manualSaveFlagsRef.current[questionId] = true;
      
      logger.debug('Saving response for question', { questionId });
      
      const result = await saveManagerRef.current.saveResponse(questionId, response, {
        skipVerification: false,
        maxRetries: 3
      });
      
      // FIX 1: Check if component is mounted before state update
      if (!isMountedRef.current) {
        return;
      }
      
      if (result.success) {
        logger.debug('Successfully saved response for question', { questionId });
        // Reset auto-save failed state if manual save succeeds
        if (autoSaveFailed) {
          setAutoSaveFailed(false);
        }
        // Flag will be cleared in status callback when status becomes 'saved'
      } else if (!result.cancelled) {
        // Only show error if not cancelled (cancelled means newer save is in progress)
        // Use enhanced error message from save manager
        const errorMsg = result.error?.message || 'Failed to save your answer. Please check your internet connection and try again.';
        // Clear manual save flag on error (PRIORITY 1 FIX)
        delete manualSaveFlagsRef.current[questionId];
        
        // Show appropriate error message based on error type
        if (result.error?.isServiceUnavailable) {
          alert(`⚠️ ${errorMsg}\n\nYour answer is queued and will be saved automatically when the service is available.`);
        } else if (result.error?.isNetworkError) {
          alert(`⚠️ ${errorMsg}\n\nYour answer is queued and will be saved automatically when your connection returns.`);
        } else if (result.error?.isServerError) {
          alert(`⚠️ ${errorMsg}\n\nPlease try saving again in a moment.`);
        } else if (result.error?.isAuthError) {
          alert(`⚠️ ${errorMsg}\n\nPlease refresh the page and log in again.`);
        } else if (result.error?.csrfTokenMissing) {
          alert(`⚠️ ${errorMsg}\n\nThis may be due to a network issue. Please check your connection and try again, or refresh the page.`);
        } else {
          alert(`⚠️ ${errorMsg}`);
        }
      } else {
        // Save was cancelled (newer save in progress)
        // Clear manual save flag (PRIORITY 2 FIX)
        delete manualSaveFlagsRef.current[questionId];
      }
    } catch (err) {
      logger.error('Error saving response for question', { questionId, error: err });
      // Clear manual save flag on error (PRIORITY 1 FIX)
      delete manualSaveFlagsRef.current[questionId];
      alert('Failed to save your answer. Please check your internet connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = useCallback(async (isAutoSubmit = false) => {
    // CRITICAL FIX: For auto-submit, don't block on saving flag
    // We'll wait for saves to complete below, but we must proceed with submission
    // even if saves are in progress to prevent timer expiration without submission
    if (!isAutoSubmit && saving) return;

    // Check if course is inactive (only for course-based quizzes)
    // Note: Backend will handle override for specific users (e.g., shahab.sayeedi@gmail.com)
    // Frontend check is informational only - backend has final say
    if (attempt?.course_id && attempt?.assignment?.course_is_active === false) {
      // Don't block here - let backend handle the override logic
      // The error will be shown if backend rejects it
    }

    setSaving(true);
    setError(null);

    try {
      // CRITICAL FIX: Clear any stale 'saving' statuses before submission
      // This prevents blocking submission due to stale statuses that haven't been cleared
      setSaveStatuses(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        
        // Clear any 'saving' statuses that are older than 2 seconds (likely stale)
        const now = Date.now();
        Object.keys(updated).forEach(questionId => {
          const status = updated[questionId];
          if (status?.status === 'saving' && status?.timestamp) {
            const age = now - status.timestamp;
            // If status is older than 2 seconds, it's likely stale - mark as saved
            if (age > 2000) {
              updated[questionId] = {
                status: 'saved',
                timestamp: now,
                error: null
              };
              hasChanges = true;
            }
          }
        });
        
        return hasChanges ? updated : prev;
      });
      
      // FIX 1: Clear all pending debounce timers FIRST to ensure all answers are included
      Object.keys(debounceTimersRef.current).forEach(questionId => {
        const timer = debounceTimersRef.current[questionId];
        if (timer) {
          clearTimeout(timer);
          delete debounceTimersRef.current[questionId];
        }
      });
      logger.debug('Cleared all pending debounced saves before submission');

      // CRITICAL FIX: If in review mode, save current question before submission
      // This ensures any edits made in review mode are saved
      if (isReviewMode && currentQuestionIndex >= 0 && saveManagerRef.current && quiz?.questions) {
        await saveCurrentQuestionBeforeNavigation();
      }

      // FIX #9: Improved auto-submit handling with longer timeout and final save attempt
      // Wait for any active saves to complete before submitting
      // For auto-submit, use longer timeout (8 seconds) to give saves more time to complete
      const saveTimeout = isAutoSubmit ? 8000 : 5000;
      if (saveManagerRef.current) {
        const savesCompleted = await saveManagerRef.current.waitForSaves(saveTimeout);
        if (!savesCompleted) {
          logger.warn('Some saves may not have completed before submission', { timeout: saveTimeout });
          
          // FIX #9: For auto-submit, make one final attempt to save all pending responses
          if (isAutoSubmit) {
            logger.warn('Auto-submit: Making final save attempt before proceeding');
            
            // Get all responses that haven't been saved yet
            const allResponses = responsesRef.current || responses;
            const unsavedResponses = {};
            
            if (quiz?.questions) {
              for (const [questionId, response] of Object.entries(allResponses)) {
                const question = quiz.questions.find(q => q.id === questionId);
                if (question && response && hasValidAnswer(question.type, response)) {
                  // Check if this response has been saved
                  const saveStatus = saveManagerRef.current.getSaveStatus(questionId);
                  if (saveStatus.status !== 'saved') {
                    unsavedResponses[questionId] = response;
                  }
                }
              }
            }
            
            if (Object.keys(unsavedResponses).length > 0) {
              logger.warn('Auto-submit: Attempting to save unsaved responses', {
                count: Object.keys(unsavedResponses).length
              });
              
              // Try to save with a short timeout (2 seconds per response, max 10 seconds total)
              try {
                await Promise.race([
                  saveManagerRef.current.saveAllResponses(unsavedResponses, {
                    skipVerification: true, // Skip verification for speed
                    skipUnchanged: false // Force save even if marked as unchanged
                  }),
                  new Promise(resolve => setTimeout(resolve, 10000)) // 10 second max
                ]);
              } catch (finalSaveError) {
                logger.error('Auto-submit: Final save attempt failed', { error: finalSaveError });
              }
            }
            
            logger.warn('Auto-submit proceeding to prevent timer expiration');
          }
        }
      }

      // Save all responses before submitting using save manager
      const saveErrors = [];

      if (saveManagerRef.current) {
        // FIX 2: Use both state and ref to get latest answers (prevents race condition)
        // This ensures we capture answers even if state update is pending
        const allResponses = responsesRef.current || responses;
        
        // CRITICAL FIX: Use question-type-specific validation to filter responses
        // Only save responses that have actual answers selected/entered
        const responsesToSave = {};
        if (quiz?.questions) {
          for (const [questionId, response] of Object.entries(allResponses)) {
            const question = quiz.questions.find(q => q.id === questionId);
            if (question && response && hasValidAnswer(question.type, response)) {
              responsesToSave[questionId] = response;
            }
          }
        }
        
        if (Object.keys(responsesToSave).length > 0) {
          const result = await saveManagerRef.current.saveAllResponses(responsesToSave, {
            skipVerification: false
          });
          
          // FIX 3: Verify saves completed before allowing submission
          // Check for failures
          let hasFailures = false;
          if (result.results) {
            for (const resultItem of result.results) {
              if (!resultItem.success && !resultItem.skipped) {
                saveErrors.push(resultItem.questionId);
                hasFailures = true;
              }
            }
          } else if (result.failed > 0) {
            // If results array not available, check failed count
            hasFailures = true;
            logger.warn('Some responses failed to save', { failed: result.failed });
          }
          
          // FIX 3: Block submission if saves failed (unless auto-submit)
          // For auto-submit, proceed anyway to prevent timer expiration without submission
          if (hasFailures && !isAutoSubmit) {
            const errorMessage = saveErrors.length > 0
              ? `Failed to save ${saveErrors.length} answer(s) before submission. Please try again.`
              : `Failed to save some answers. Please try again.`;
            setError(errorMessage);
            setSaving(false);
            logger.error('Submission blocked due to save failures', { saveErrors });
            return; // Don't proceed with submission
          } else if (hasFailures && isAutoSubmit) {
            logger.warn('Auto-submit proceeding despite save failures to prevent timer expiration');
          }
        }
      } else {
        // Fallback to direct API calls if save manager not available
        // FIX 2: Use both state and ref in fallback too
        const allResponses = responsesRef.current || responses;
        
        for (const [questionId, response] of Object.entries(allResponses)) {
          if (response && (response.selected_option_id || response.answer_text)) {
            try {
              // Ensure answer_text is properly set for multiple choice questions
              let answerText = response.answer_text || '';
              if (!answerText && response.selected_option_ids && Array.isArray(response.selected_option_ids)) {
                // If answer_text is missing but selected_option_ids exists, convert it
                answerText = JSON.stringify(response.selected_option_ids);
              }
              
              await quizApiService.saveResponse(attemptId, {
                question_id: parseInt(questionId),
                selected_option_id: response.selected_option_id,
                answer_text: answerText
              });
            } catch (saveErr) {
              logger.error('Failed to save response for question', { questionId, error: saveErr });
              saveErrors.push(questionId);
              // Continue saving other responses even if one fails
            }
          }
        }
      }

      // FIX 3: Block submission if saves failed (for fallback path too, unless auto-submit)
      if (saveErrors.length > 0 && !isAutoSubmit) {
        const errorMessage = `Failed to save ${saveErrors.length} answer(s) before submission. Please try again.`;
        setError(errorMessage);
        setSaving(false);
        logger.error('Submission blocked due to save failures', { saveErrors });
        return; // Don't proceed with submission
      } else if (saveErrors.length > 0 && isAutoSubmit) {
        logger.warn('Auto-submit proceeding despite save failures to prevent timer expiration');
      }

      // CRITICAL FIX 1: Use auto-submit endpoint for single-attempt quizzes when auto-submitting
      // Submit the attempt - use auto-submit endpoint for single-attempt quizzes when auto-submitting
      const response = isAutoSubmit && attempt?.assignment?.attempts_allowed === 1
        ? await quizApiService.autoSubmitAttempt(attemptId)
        : await quizApiService.submitAttempt(attemptId);
      
      if (response.success) {
        // FIX 8: Clear error state on successful submission
        if (isMountedRef.current) {
          setError(null);
        }
        
        // CRITICAL FIX: Always navigate to results page after submission
        // The results page will handle showing appropriate message if immediate_feedback is false
        navigate(`/student/result/${attemptId}`, { 
          state: { results: response.data } 
        });
        
        // CRITICAL FIX 2: Return success for auto-submit to enable retry logic
        if (isAutoSubmit) {
          return { success: true };
        }
      } else {
        const errorMsg = 'Failed to submit quiz';
        setError(errorMsg);
        // CRITICAL FIX 2: Throw error for auto-submit to enable retry logic
        if (isAutoSubmit) {
          throw new Error(errorMsg);
        }
      }
    } catch (err) {
      logger.error('Error submitting quiz', { error: err, attemptId });
      const errorMsg = err.message || 'Failed to submit quiz. Please try again.';
      setError(errorMsg);
      // CRITICAL FIX 2: Re-throw error for auto-submit to enable retry logic
      if (isAutoSubmit) {
        throw err;
      }
    } finally {
      setSaving(false);
    }
  }, [attemptId, responses, navigate, saving, isReviewMode, currentQuestionIndex, quiz, saveCurrentQuestionBeforeNavigation, attempt, hasValidAnswer]);

  // MEDIUM PRIORITY FIX 3: Add retry logic for failed auto-submit
  const submitWithRetry = useCallback(async (maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        logger.debug('Auto-Submit attempt', { attempt: i + 1, maxRetries });
        // CRITICAL FIX 2: handleSubmit now throws errors for auto-submit, so retry will work
        const result = await handleSubmit(true); // Pass isAutoSubmit flag
        logger.info('Auto-Submit successfully submitted', { attemptId });
        return result; // Success
      } catch (error) {
        logger.error('Auto-Submit attempt failed', { attempt: i + 1, maxRetries, error });
        if (i < maxRetries - 1) {
          const delay = 1000 * Math.pow(2, i); // Exponential backoff: 1s, 2s, 4s
          logger.warn('Auto-Submit retrying', { delay, attempt: i + 1 });
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          logger.error('Auto-Submit all retries failed', { maxRetries });
          // Show error to user
          // FIX 5: Check mount before state update
          if (isMountedRef.current) {
            setError('Failed to auto-submit quiz. Please submit manually.');
            setSaving(false);
            setIsAutoSubmitting(false); // FIX 1: Use state as single source of truth
          }
          throw error;
        }
      }
    }
  }, [handleSubmit, setError, setSaving, attemptId]);

  // Set the ref value for handleSubmit
  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // FIX 5: Set up mount tracking
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // FIX 3: Cleanup timeouts on unmount
      if (timerExtendedTimeoutRef.current) {
        clearTimeout(timerExtendedTimeoutRef.current);
        timerExtendedTimeoutRef.current = null;
      }
      if (fallbackSubmissionTimeoutRef.current) {
        clearTimeout(fallbackSubmissionTimeoutRef.current);
        fallbackSubmissionTimeoutRef.current = null;
      }
    };
  }, []);

  // Track if timer is paused due to saves
  const [timerPaused, setTimerPaused] = useState(false);
  const timerIntervalRef = useRef(null);
  // FIX 1: Use state as single source of truth, ref for cleanup checks
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const [timerExtended, setTimerExtended] = useState(false);
  // FIX 3: Store timeout IDs for cleanup
  const timerExtendedTimeoutRef = useRef(null);
  const fallbackSubmissionTimeoutRef = useRef(null);
  
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0) {
      timerIntervalRef.current = setInterval(async () => {
        // Check if saves are in progress
        const hasActiveSaves = saveManagerRef.current?.hasActiveSaves() || false;
        
        if (hasActiveSaves) {
          // Pause timer while saves are in progress
          if (!timerPaused) {
            setTimerPaused(true);
            logger.debug('Quiz Timer paused due to active saves');
          }
          // Don't decrement timer while paused - this extends the timer automatically
          return;
        } else {
          // Resume timer if it was paused
          if (timerPaused) {
            setTimerPaused(false);
            logger.debug('Quiz Timer resumed after saves completed');
          }
        }
        
        setTimeRemaining(prev => {
          // MEDIUM ISSUE 17 FIX: Account for clock skew in timer calculation
          // Apply clock skew offset if available (calculated on initial load)
          const clockSkewOffset = window.quizClockSkewOffset || 0;
          const adjustedPrev = prev + Math.floor(clockSkewOffset / 1000); // Convert ms to seconds
          
          // Submit when timer reaches 2 seconds to ensure submission happens before timeout
          // This gives a buffer to account for network latency and server processing time
          if (adjustedPrev <= 2) {
            // Clear the interval first to prevent multiple submissions
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            
            // HIGH PRIORITY FIX 2: Force submission after timeout with retry logic
            (async () => {
              logger.info('Timer expired, starting submission process', { attemptId });
              
              if (saveManagerRef.current) {
                const hasActiveSaves = saveManagerRef.current.hasActiveSaves();
                logger.debug('Checking for active saves before auto-submit', { hasActiveSaves });
                
                // Wait for saves with timeout (3 seconds for auto-submit)
                const savesCompleted = await saveManagerRef.current.waitForSaves(3000);
                if (!savesCompleted) {
                  logger.warn('Some saves may not have completed before auto-submit');
                  // Verify saves one more time before submitting
                  const stillHasSaves = saveManagerRef.current.hasActiveSaves();
                  logger.debug('Still has active saves after wait', { stillHasSaves });
                  
                  if (stillHasSaves) {
                    logger.warn('Active saves still in progress, extending timer by 3 seconds');
                    // UI FIX 4: Show extended timer warning
                    setTimerExtended(true);
                    // FIX 3: Clear existing timeout before setting new one
                    if (timerExtendedTimeoutRef.current) {
                      clearTimeout(timerExtendedTimeoutRef.current);
                    }
                    timerExtendedTimeoutRef.current = setTimeout(() => {
                      if (isMountedRef.current) {
                        setTimerExtended(false);
                      }
                      timerExtendedTimeoutRef.current = null;
                    }, 5000);
                    // Extend timer by 3 seconds to allow saves to complete
                    setTimeRemaining(3);
                    // FIX 4: Clear existing interval before creating new one
                    if (timerIntervalRef.current) {
                      clearInterval(timerIntervalRef.current);
                      timerIntervalRef.current = null;
                    }
                    // Restart interval
                    timerIntervalRef.current = setInterval(() => {
                      setTimeRemaining(prev => {
                        if (prev <= 0) {
                          if (timerIntervalRef.current) {
                            clearInterval(timerIntervalRef.current);
                            timerIntervalRef.current = null;
                          }
                          // FIX 6: Check if submission already in progress
                          if (isAutoSubmitting) {
                            logger.warn('Submission already in progress, skipping extended timer submission');
                            return 0;
                          }
                          
                          // CRITICAL FIX 1: Wrap in async IIFE with error handling
                          logger.debug('Extended timer expired, attempting submission...');
                          setIsAutoSubmitting(true); // FIX 1: Use state as single source of truth
                          let fallbackTriggered = false;
                          
                          (async () => {
                            // FIX 5: Check if component still mounted
                            if (!isMountedRef.current) {
                              setIsAutoSubmitting(false);
                              return;
                            }
                            
                            // Add timeout to detect stuck submissions (30 seconds max)
                            const submissionTimeout = setTimeout(() => {
                              if (isMountedRef.current && isAutoSubmitting) {
                                logger.error('Extended timer submission timeout');
                                setError('Submission is taking too long. Please try submitting manually.');
                                setIsAutoSubmitting(false);
                              }
                            }, 30000);
                            
                            try {
                              await submitWithRetry();
                              clearTimeout(submissionTimeout);
                              logger.info('Extended timer submission succeeded');
                              // FIX 5: Check mount before state update
                              if (isMountedRef.current) {
                                setIsAutoSubmitting(false);
                              }
                            } catch (error) {
                              clearTimeout(submissionTimeout);
                              logger.error('Extended timer submission failed', { error });
                              // MEDIUM PRIORITY FIX 3: Fallback submission after delay
                              fallbackTriggered = true;
                              // FIX 3: Store timeout ID for cleanup
                              if (fallbackSubmissionTimeoutRef.current) {
                                clearTimeout(fallbackSubmissionTimeoutRef.current);
                              }
                              fallbackSubmissionTimeoutRef.current = setTimeout(async () => {
                                // FIX 5: Check if component still mounted
                                if (!isMountedRef.current) {
                                  setIsAutoSubmitting(false);
                                  fallbackSubmissionTimeoutRef.current = null;
                                  return;
                                }
                                
                                // Add timeout for fallback submission too
                                const fallbackTimeout = setTimeout(() => {
                                  if (isMountedRef.current && isAutoSubmitting) {
                                    logger.error('Fallback submission timeout');
                                    setError('Submission is taking too long. Please try submitting manually.');
                                    setIsAutoSubmitting(false);
                                  }
                                }, 30000);
                                
                                try {
                                  logger.warn('Attempting fallback submission after extended timer failure...');
                                  await submitWithRetry();
                                  clearTimeout(fallbackTimeout);
                                  logger.info('Fallback submission succeeded');
                                  // FIX 5: Check mount before state update
                                  if (isMountedRef.current) {
                                    setIsAutoSubmitting(false);
                                  }
                                } catch (fallbackError) {
                                  clearTimeout(fallbackTimeout);
                                  logger.error('Fallback submission also failed', { error: fallbackError });
                                  // FIX 5: Check mount before state update
                                  if (isMountedRef.current) {
                                    setError('Failed to auto-submit quiz. Please submit manually.');
                                    setSaving(false);
                                    // Keep overlay visible so user can see error and manual submit button
                                  }
                                } finally {
                                  fallbackSubmissionTimeoutRef.current = null;
                                }
                              }, 2000);
                            } finally {
                              // FIX 2: Only reset if fallback wasn't triggered
                              if (!fallbackTriggered && isMountedRef.current) {
                                setIsAutoSubmitting(false);
                              }
                            }
                          })();
                          return 0;
                        }
                        return prev - 1;
                      });
                    }, 1000);
                    return;
                  }
                } else {
                  logger.debug('All saves completed successfully');
                }
              }
              
              // FIX 6: Check if submission already in progress
              if (isAutoSubmitting) {
                logger.warn('Submission already in progress, skipping');
                return;
              }
              
              // MEDIUM PRIORITY FIX 3: Use retry logic for auto-submit
              logger.debug('Proceeding with submission...');
              setIsAutoSubmitting(true); // FIX 1: Use state as single source of truth
              
              // FIX 5: Check if component still mounted
              if (!isMountedRef.current) {
                setIsAutoSubmitting(false);
                return;
              }
              
              // Add timeout to detect stuck submissions (30 seconds max)
              const submissionTimeout = setTimeout(() => {
                if (isMountedRef.current && isAutoSubmitting) {
                  logger.error('Submission timeout - taking too long');
                  setError('Submission is taking too long. Please try submitting manually.');
                  setIsAutoSubmitting(false);
                }
              }, 30000);
              
              try {
                await submitWithRetry();
                clearTimeout(submissionTimeout);
                logger.info('Submission succeeded');
                // FIX 5: Check mount before state update
                if (isMountedRef.current) {
                  setIsAutoSubmitting(false);
                }
              } catch (error) {
                clearTimeout(submissionTimeout);
                logger.error('Submission failed', { error });
                // Error already handled in submitWithRetry, but ensure overlay is cleared
                // FIX 5: Check mount before state update
                if (isMountedRef.current) {
                  // Keep isAutoSubmitting true if error message is shown (so overlay stays)
                  // It will be cleared when user clicks manual submit button
                  if (!error || !error.message || !error.message.includes('auto-submit')) {
                    setIsAutoSubmitting(false);
                  }
                }
              }
            })();
            
            return 0;
          }
          
          // MEDIUM ISSUE 17 FIX: Periodic server time sync for clock skew correction
          // Sync every 30 seconds to account for clock drift
          const syncInterval = 30; // seconds
          if (prev % syncInterval === 0 && prev > 0) {
            // Sync with server time periodically
            (async () => {
              try {
                const serverTime = await quizApiService.getServerTime();
                const clientTime = new Date();
                const newClockSkew = serverTime.getTime() - clientTime.getTime();
                window.quizClockSkewOffset = newClockSkew;
                logger.debug('Clock skew updated', { skew: Math.floor(newClockSkew / 1000) });
              } catch (e) {
                logger.warn('Failed to sync server time', { error: e });
              }
            })();
          }
          
          // Clock sync happens on initial load, timer decrements normally
          // Apply clock skew offset for more accurate timer (reuse clockSkewOffset from above)
          const skewSeconds = Math.floor(clockSkewOffset / 1000);
          
          // If clock skew is significant (>2 seconds), adjust timer
          if (Math.abs(skewSeconds) > 2) {
            return prev - 1 + (skewSeconds > 0 ? -1 : 1); // Adjust based on skew direction
          }
          
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
        
        // HIGH PRIORITY FIX 2: Ensure submission completes before unmount
        // If timer expired but submission not complete, try to submit
        // FIX 6: Check state instead of ref for duplicate prevention
        if (timeRemaining === 0 && !isAutoSubmitting) {
          logger.warn('Component unmounting with expired timer, attempting submission...', { attemptId });
          setIsAutoSubmitting(true); // FIX 1: Use state as single source of truth
          // Use beforeunload handler's submit function if available, otherwise try submitWithRetry
          (async () => {
            try {
              await submitWithRetry();
              logger.info('Cleanup submission succeeded');
            } catch (error) {
              logger.error('Cleanup submission failed', { error });
              // Submission will be handled by beforeunload handler as fallback
            } finally {
              // FIX 5: Only update if still mounted (though unlikely at this point)
              if (isMountedRef.current) {
                setIsAutoSubmitting(false);
              }
            }
          })();
        }
      };
    }
  }, [timeRemaining, handleSubmit, timerPaused, submitWithRetry, attemptId, isAutoSubmitting]);

  // Prevent console access and source viewing
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0) {
      // Disable right-click context menu
      const handleContextMenu = (e) => {
        e.preventDefault();
        return false;
      };

      // Disable F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+U
      const handleKeyDown = (e) => {
        // F12
        if (e.keyCode === 123) {
          e.preventDefault();
          return false;
        }
        // Ctrl+Shift+I (DevTools)
        if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
          e.preventDefault();
          return false;
        }
        // Ctrl+Shift+J (Console)
        if (e.ctrlKey && e.shiftKey && e.keyCode === 74) {
          e.preventDefault();
          return false;
        }
        // Ctrl+U (View Source)
        if (e.ctrlKey && e.keyCode === 85) {
          e.preventDefault();
          return false;
        }
        // Ctrl+Shift+C (Element Inspector)
        if (e.ctrlKey && e.shiftKey && e.keyCode === 67) {
          e.preventDefault();
          return false;
        }
        // Ctrl+S (Save Page)
        if (e.ctrlKey && e.keyCode === 83) {
          e.preventDefault();
          return false;
        }
        // Ctrl+A (Select All)
        if (e.ctrlKey && e.keyCode === 65) {
          e.preventDefault();
          return false;
        }
        // Ctrl+P (Print)
        if (e.ctrlKey && e.keyCode === 80) {
          e.preventDefault();
          return false;
        }
      };

      // Disable text selection
      const handleSelectStart = (e) => {
        e.preventDefault();
        return false;
      };

      // Disable drag
      const handleDragStart = (e) => {
        e.preventDefault();
        return false;
      };

      // Add event listeners
      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('selectstart', handleSelectStart);
      document.addEventListener('dragstart', handleDragStart);

      // Disable console methods
      const disableConsole = () => {
        const noop = () => {};
        const methods = ['log', 'debug', 'info', 'warn', 'error', 'trace', 'dir', 'group', 'groupEnd', 'time', 'timeEnd', 'profile', 'profileEnd', 'count', 'clear', 'assert'];
        methods.forEach(method => {
          if (window.console && window.console[method]) {
            window.console[method] = noop;
          }
        });
      };

      disableConsole();

      // Cleanup
      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('selectstart', handleSelectStart);
        document.removeEventListener('dragstart', handleDragStart);
      };
    }
  }, [timeRemaining]);

  // Auto-submit on browser close/refresh/navigation (timed quizzes only)
  useEffect(() => {
    // Only enable auto-submit for timed quizzes (timeRemaining must be set and > 0)
    // Untimed quizzes (even single-attempt) should NOT auto-submit on page unload
    const isTimedQuiz = timeRemaining !== null && timeRemaining > 0;
    const hasTimeLimit = attempt?.assignment?.time_limit_minutes && attempt.assignment.time_limit_minutes > 0;
    
    // Only proceed if this is actually a timed quiz
    if (!(isTimedQuiz && hasTimeLimit)) {
      return;
    }
    
    const handleBeforeUnload = (event) => {
      // CRITICAL FIX: Only auto-submit if timer has expired or is very close to expiring (within 5 seconds)
      // This prevents auto-submitting when user navigates away intentionally (e.g., clicking "Back to Quizzes")
      // Only auto-submit if timer has actually expired or is about to expire
      // Use current timeRemaining from state (closure will have latest value since effect re-runs on change)
      const currentTimeRemaining = timeRemaining;
      const shouldAutoSubmit = currentTimeRemaining !== null && currentTimeRemaining <= 5;
      
      if (!shouldAutoSubmit) {
        // Timer hasn't expired yet - just save, don't submit
        // This allows user to navigate away and come back without losing their attempt
        logger.debug('Timer not expired, saving progress only (not auto-submitting)', { 
          timeRemaining: currentTimeRemaining,
          attemptId 
        });
        
        // Still save responses, but don't submit
        const saveAllResponses = async () => {
          const token = localStorage.getItem('faithinsight_token');
          if (!token) {
            logger.warn('No auth token available for unload save');
            return;
          }

          const csrfToken = csrfTokenRef.current;
          if (!csrfToken) {
            logger.warn('CSRF token not available - skipping save to avoid 403 error');
            return;
          }

          if (!quiz?.questions) return;
          
          for (const [questionId, response] of Object.entries(responses)) {
            const question = quiz.questions.find(q => q.id === questionId);
            if (!question || !response || !hasValidAnswer(question.type, response)) {
              continue;
            }
            
            let answerText = response.answer_text || '';
            if (!answerText && response.selected_option_ids && Array.isArray(response.selected_option_ids)) {
              answerText = JSON.stringify(response.selected_option_ids);
            }

            const data = JSON.stringify({
              question_id: parseInt(questionId),
              selected_option_id: response.selected_option_id,
              answer_text: answerText
            });

            const apiBaseURL = apiService.baseURL || '/portal/api';
            const url = `${window.location.origin}${apiBaseURL}/quizzes/student/attempts/${attemptId}/response`;
            
            const headers = {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
              'X-Token-Version': tokenRefreshManager.getTokenVersion().toString(),
              'X-CSRF-Token': csrfToken
            };
            
            fetch(url, {
              method: 'POST',
              headers,
              body: data,
              keepalive: true
            }).catch(err => {
              logger.warn('Error saving response during unload (non-blocking)', { error: err, questionId });
            });
          }
        };
        
        saveAllResponses();
        return; // Don't show warning or submit
      }
      
      // Timer has expired or is about to expire - auto-submit
      logger.info('Timer expired or close to expiring, auto-submitting on page unload', { 
        timeRemaining: currentTimeRemaining,
        attemptId 
      });
      
      // Save all responses before leaving using fetch with keepalive for reliability
      const saveAllResponses = async () => {
        const token = localStorage.getItem('faithinsight_token');
        if (!token) {
          logger.warn('No auth token available for unload save');
          return;
        }

        // CRITICAL FIX: Don't try to save if CSRF token is missing - it will fail with 403
        const csrfToken = csrfTokenRef.current;
        if (!csrfToken) {
          logger.warn('CSRF token not available - skipping save to avoid 403 error');
          // Don't attempt save without CSRF token - it will fail with 403
          // The save manager's periodic save will handle it when the page is active
          return;
        }

        // Check if token needs refresh before unload
        if (tokenRefreshManager.isRefreshTokenExpiringSoon()) {
          // Try to refresh token before saving (use keepalive)
          // Note: sendBeacon doesn't support auth, so we'll try to refresh synchronously if possible
          // For now, just use current token and hope it's still valid
          // Token refresh during unload is handled by the save manager's retry logic
        }

        // CRITICAL FIX: Only save responses with valid answers
        if (!quiz?.questions) return;
        
        for (const [questionId, response] of Object.entries(responses)) {
          const question = quiz.questions.find(q => q.id === questionId);
          if (!question || !response || !hasValidAnswer(question.type, response)) {
            continue; // Skip invalid or empty responses
          }
          
          // Ensure answer_text is properly set (same logic as handleSubmit)
          let answerText = response.answer_text || '';
          if (!answerText && response.selected_option_ids && Array.isArray(response.selected_option_ids)) {
            answerText = JSON.stringify(response.selected_option_ids);
          }

          // Use fetch with keepalive for reliable delivery during page unload
          const data = JSON.stringify({
            question_id: parseInt(questionId),
            selected_option_id: response.selected_option_id,
            answer_text: answerText
          });

          // CRITICAL FIX: Use correct API baseURL instead of hardcoded /api
          const apiBaseURL = apiService.baseURL || '/portal/api';
          const url = `${window.location.origin}${apiBaseURL}/quizzes/student/attempts/${attemptId}/response`;
          
          // Build headers with CSRF token (we've already checked it exists above)
          const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Token-Version': tokenRefreshManager.getTokenVersion().toString(),
            'X-CSRF-Token': csrfToken
          };
          
          // fetch with keepalive is more reliable than sendBeacon for authenticated requests
          fetch(url, {
            method: 'POST',
            headers,
            body: data,
            keepalive: true
          })
          .then(async (response) => {
            if (!response.ok) {
              // Log error details for debugging
              try {
                const errorData = await response.json().catch(() => ({}));
                logger.warn('HTTP error during unload save', { status: response.status, questionId, errorData });
              } catch (e) {
                logger.warn('HTTP error during unload save (could not parse error)', { status: response.status, questionId });
              }
            }
          })
          .catch(err => {
            // Silently handle errors - can't show UI feedback during unload
            logger.warn('Error saving response during unload (non-blocking)', { error: err, questionId });
          });
        }
      };

      // Submit the quiz using fetch with keepalive for reliability
      const submitQuiz = () => {
        const token = localStorage.getItem('faithinsight_token');
        if (!token) {
          logger.warn('No auth token available for unload submit');
          return;
        }

        // CRITICAL FIX: Don't try to submit if CSRF token is missing - it will fail with 403
        const csrfToken = csrfTokenRef.current;
        if (!csrfToken) {
          logger.warn('CSRF token not available - skipping submit to avoid 403 error');
          // Don't attempt submit without CSRF token - it will fail with 403
          // The user will need to submit manually when they return
          return;
        }

        try {
          // CRITICAL FIX: Use correct API baseURL instead of hardcoded /api
          const apiBaseURL = apiService.baseURL || '/portal/api';
          const endpoint = attempt && attempt.assignment && attempt.assignment.attempts_allowed === 1
            ? `${apiBaseURL}/quizzes/student/attempts/${attemptId}/auto-submit`
            : `${apiBaseURL}/quizzes/student/attempts/${attemptId}/submit`;

          // Build headers with CSRF token (we've already checked it exists above)
          const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-CSRF-Token': csrfToken
          };

          // Use fetch with keepalive for reliable submission during unload
          fetch(`${window.location.origin}${endpoint}`, {
            method: 'POST',
            headers,
            keepalive: true
          })
          .then(async (response) => {
            if (!response.ok) {
              // Log error details for debugging
              try {
                const errorData = await response.json().catch(() => ({}));
                logger.warn('HTTP error during unload submit', { status: response.status, errorData });
              } catch (e) {
                logger.warn('HTTP error during unload submit (could not parse error)', { status: response.status });
              }
            }
          })
          .catch(err => {
            // Silently handle errors - can't show UI feedback during unload
            logger.warn('Error submitting quiz during unload (non-blocking)', { error: err });
          });
        } catch (err) {
          // Silently handle errors - can't show UI feedback during unload
          logger.warn('Error submitting quiz during unload (non-blocking)', { error: err });
        }
      };

      // Show warning message only if timer has expired
      event.preventDefault();
      event.returnValue = 'Your quiz will be automatically submitted if you leave this page. Are you sure you want to continue?';
      
      // CRITICAL FIX: Save and submit with CSRF tokens (synchronous - uses cached token)
      // The keepalive flag ensures requests complete even after page unloads
      saveAllResponses();
      submitQuiz();

      return event.returnValue;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden (tab switched, minimized, etc.)
        // Save responses but don't submit yet
        const saveAllResponses = async () => {
          // CRITICAL FIX: Only save responses with valid answers
          if (!quiz?.questions) return;
          
          for (const [questionId, response] of Object.entries(responses)) {
            const question = quiz.questions.find(q => q.id === questionId);
            if (!question || !response || !hasValidAnswer(question.type, response)) {
              continue; // Skip invalid or empty responses
            }
            
            try {
              // Ensure answer_text is properly set (same logic as handleSubmit)
              let answerText = response.answer_text || '';
              if (!answerText && response.selected_option_ids && Array.isArray(response.selected_option_ids)) {
                answerText = JSON.stringify(response.selected_option_ids);
              }
              
              await quizApiService.saveResponse(attemptId, {
                question_id: parseInt(questionId),
                selected_option_id: response.selected_option_id,
                answer_text: answerText
              });
            } catch (err) {
              logger.error('Error saving response on visibility change', { error: err, questionId });
            }
          }
        };
        saveAllResponses();
      }
    };

      // Add event listeners
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    // If not a timed quiz, do nothing - untimed quizzes should not auto-submit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRemaining, responses, attemptId, attempt, quiz, hasValidAnswer]);

  // Save progress on browser close/refresh for untimed quizzes
  useEffect(() => {
    if (autoSaveEnabled && timeRemaining === null) {
      const handleBeforeUnload = (event) => {
        // Save all responses before leaving (don't submit)
        const saveAllResponses = async () => {
          // CRITICAL FIX: Only save responses with valid answers
          if (!quiz?.questions) return;
          
          for (const [questionId, response] of Object.entries(responses)) {
            const question = quiz.questions.find(q => q.id === questionId);
            if (!question || !response || !hasValidAnswer(question.type, response)) {
              continue; // Skip invalid or empty responses
            }
            
            try {
              await quizApiService.saveResponse(attemptId, {
                question_id: parseInt(questionId),
                selected_option_id: response.selected_option_id,
                answer_text: response.answer_text
              });
            } catch (err) {
              logger.error('Error saving response on unload', { error: err, questionId });
            }
          }
        };

        // Show informational message
        event.preventDefault();
        event.returnValue = 'Your progress will be saved. You can resume this quiz later.';
        
        // Save responses in the background
        saveAllResponses();

        return event.returnValue;
      };

      const handleVisibilityChange = () => {
        if (document.hidden) {
          // Page is hidden (tab switched, minimized, etc.)
          // Save responses
          const saveAllResponses = async () => {
            // CRITICAL FIX: Only save responses with valid answers
            if (!quiz?.questions) return;
            
            for (const [questionId, response] of Object.entries(responses)) {
              const question = quiz.questions.find(q => q.id === questionId);
              if (!question || !response || !hasValidAnswer(question.type, response)) {
                continue; // Skip invalid or empty responses
              }
              
              try {
                await quizApiService.saveResponse(attemptId, {
                  question_id: parseInt(questionId),
                  selected_option_id: response.selected_option_id,
                  answer_text: response.answer_text
                });
              } catch (err) {
                logger.error('Error saving response on visibility change', { error: err, questionId });
              }
            }
          };
          saveAllResponses();
        }
      };

      // Add event listeners
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Cleanup
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [autoSaveEnabled, timeRemaining, responses, attemptId, quiz, hasValidAnswer]);

  const goToQuestion = useCallback(async (index) => {
    // Save current question before navigating
    await saveCurrentQuestionBeforeNavigation();
    setCurrentQuestionIndex(index);
  }, [saveCurrentQuestionBeforeNavigation]);

  const enterReviewMode = useCallback(async () => {
    // CRITICAL FIX: Save current question before entering review mode
    // This prevents the last question from losing its answer
    try {
      await saveCurrentQuestionBeforeNavigation();
      
      // CRITICAL FIX: Wait a short time for save status to update, then clear any stale 'saving' status
      // This prevents the submit button from being disabled due to stale 'saving' status
      const currentQuestion = quiz?.questions?.[currentQuestionIndex];
      if (currentQuestion) {
        const questionId = currentQuestion.id;
        
        // Wait up to 1 second for save status to update (status updates are async)
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Clear any stale 'saving' status that might still be there
        // This is a safety net in case the status update is delayed
        setSaveStatuses(prev => {
          const updated = { ...prev };
          // Only clear if it's still 'saving' (might have been updated to 'saved' by now)
          if (updated[questionId]?.status === 'saving') {
            // Change to 'saved' instead of deleting, so user sees it was saved
            // This prevents the submit button from being disabled
            updated[questionId] = {
              status: 'saved',
              timestamp: Date.now(),
              error: null
            };
          }
          return updated;
        });
        
        // Also clear any pending debounce timers for this question
        if (debounceTimersRef.current[questionId]) {
          clearTimeout(debounceTimersRef.current[questionId]);
          delete debounceTimersRef.current[questionId];
        }
      }
    } catch (err) {
      // If save fails (e.g., 404 - attempt not found), log but don't block review mode
      // The save manager will have already updated the status to 'failed'
      logger.warn('Failed to save current question before entering review mode', { error: err });
      // Still allow entering review mode - user can see their answers
    }
    setIsReviewMode(true);
  }, [saveCurrentQuestionBeforeNavigation, quiz, currentQuestionIndex]);

  const exitReviewMode = useCallback(async () => {
    // CRITICAL FIX: Save all answers before exiting review mode
    // This ensures any edits made in review mode are saved
    if (saveManagerRef.current && quiz?.questions) {
      // Clear all pending debounce timers
      Object.keys(debounceTimersRef.current).forEach(questionId => {
        const timer = debounceTimersRef.current[questionId];
        if (timer) {
          clearTimeout(timer);
          delete debounceTimersRef.current[questionId];
        }
      });
      
      // Get all responses (use both state and ref to capture latest)
      const allResponses = responsesRef.current || responses;
      
      // Save all responses that have answers
      const responsesToSave = {};
      for (const [questionId, response] of Object.entries(allResponses)) {
        if (response && (response.selected_option_id || response.answer_text)) {
          responsesToSave[questionId] = response;
        }
      }
      
      if (Object.keys(responsesToSave).length > 0) {
        try {
          const result = await saveManagerRef.current.saveAllResponses(responsesToSave, {
            skipVerification: true,
            maxRetries: 1
          });
          
          if (result.failed > 0) {
            logger.warn('Some answers failed to save before exiting review mode', { failed: result.failed });
            // Show warning but don't block exit - periodic save will retry
            // User can also manually save if needed
          } else {
            logger.debug('Saved all answers before exiting review mode');
          }
        } catch (err) {
          logger.warn('Error saving answers before exiting review mode', { error: err });
          // Show warning to user but don't block exit
          // Periodic save will catch it, or user can manually save
          if (err?.message) {
            logger.warn('Save error details', { error: err.message });
          }
        }
      }
    }
    
    setIsReviewMode(false);
  }, [quiz, responses]);

  const goToQuestionFromReview = useCallback(async (questionIndex) => {
    // CRITICAL FIX: Save current question before navigating, even in review mode
    // If user edited an answer in review mode, we need to save it before navigating to another question
    if (currentQuestionIndex >= 0) {
      await saveCurrentQuestionBeforeNavigation();
    }
    comingFromReviewRef.current = true;
    setIsReviewMode(false);
    setCurrentQuestionIndex(questionIndex);
  }, [currentQuestionIndex, saveCurrentQuestionBeforeNavigation]);

  // Scroll to question header when coming from review mode
  useEffect(() => {
    if (comingFromReviewRef.current && questionHeaderRef.current && !isReviewMode) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (questionHeaderRef.current) {
          const element = questionHeaderRef.current;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 20; // 20px offset from top
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          comingFromReviewRef.current = false;
        }
      }, 150);
    }
  }, [currentQuestionIndex, isReviewMode]);

  const isQuestionAnswered = (question, response) => {
    if (!response) return false;
    
    if (question.type === 'multiple_choice') {
      // Check if any options are selected (support both new and old format)
      if (response.answer_text) {
        try {
          const parsed = JSON.parse(response.answer_text);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return true;
          }
        } catch (e) {
          // Not JSON, fall through to legacy check
        }
      }
      return response.selected_option_id !== null;
    } else if (question.type === 'true_false') {
      return response.selected_option_id !== null;
    } else if (question.type === 'matching') {
      // Check if matching pairs exist in answer_text
      if (response.answer_text) {
        try {
          const pairs = JSON.parse(response.answer_text);
          if (Array.isArray(pairs) && pairs.length > 0) {
            return true;
          }
        } catch (e) {
          // Not valid JSON
        }
      }
      return false;
    } else if (question.type === 'fill_blank') {
      const blankCount = getBlankCount(question.prompt);
      
      if (blankCount === 1) {
        // Single blank - check if answer_text exists and is not empty
        return response.answer_text && response.answer_text.trim() !== '';
      } else if (blankCount > 1) {
        // Multiple blanks - check if all blanks are filled
        if (!response.answer_texts || response.answer_texts.length !== blankCount) {
          return false;
        }
        // Check if all blanks have non-empty answers
        return response.answer_texts.every(answer => answer && answer.trim() !== '');
      }
      
      return false;
    }
    
    return false;
  };

  const isBlankFilled = (response, blankIndex) => {
    if (!response || !response.answer_texts) return false;
    return response.answer_texts[blankIndex] && response.answer_texts[blankIndex].trim() !== '';
  };

  const formatAnswerForReview = (question, response) => {
    if (!response) return '';
    
    if (question.type === 'multiple_choice') {
      // Get all selected option IDs (support both new and old format)
      let selectedOptionIds = [];
      
      // Try to parse from answer_text (new format for multiple selections)
      if (response.answer_text) {
        try {
          const parsed = JSON.parse(response.answer_text);
          if (Array.isArray(parsed)) {
            selectedOptionIds = parsed;
          }
        } catch (e) {
          // Not JSON, fall through to legacy check
        }
      }
      
      // Fallback to selected_option_id (legacy single selection)
      if (selectedOptionIds.length === 0 && response.selected_option_id) {
        selectedOptionIds = [response.selected_option_id];
      }
      
      // Get all selected option texts
      const selectedOptions = selectedOptionIds
        .map(id => question.options.find(opt => opt.id === id))
        .filter(Boolean)
        .map(opt => opt.option_text);
      
      return selectedOptions.length > 0 ? selectedOptions.join(', ') : '';
    } else if (question.type === 'true_false') {
      const selectedOption = question.options.find(opt => opt.id === response.selected_option_id);
      return selectedOption ? selectedOption.option_text : '';
    } else if (question.type === 'fill_blank') {
      return response.answer_text || '';
    } else if (question.type === 'matching') {
      // Parse matching pairs from answer_text
      if (response.answer_text) {
        try {
          const pairs = JSON.parse(response.answer_text);
          if (Array.isArray(pairs) && pairs.length > 0) {
            const settings = question.settings || {};
            const leftItems = settings.leftItems || [];
            const rightItems = settings.rightItems || [];
            
            // Format pairs as "Term → Definition"
            return pairs.map(pair => {
              const left = leftItems[pair.leftIndex] || '';
              const right = rightItems[pair.rightIndex] || '';
              return `${left} → ${right}`;
            }).join('\n');
          }
        } catch (e) {
          // Not valid JSON, return empty
        }
      }
      return '';
    }
    
    return '';
  };

  const formatFillBlankPromptWithAnswers = (prompt, response) => {
    if (!prompt || !response || !response.answer_text) return prompt;
    
    // Split the answer text by pipe separator for multiple blanks
    const answers = response.answer_text.split('|');
    let formattedPrompt = prompt;
    let answerIndex = 0;
    
    // Replace each blank (underscores) with the corresponding answer
    formattedPrompt = formattedPrompt.replace(/_{1,}/g, (match) => {
      if (answerIndex < answers.length) {
        const answer = answers[answerIndex].trim();
        answerIndex++;
        return answer ? `[${answer}]` : match; // Show answer in brackets, or keep blank if empty
      }
      return match;
    });
    
    return formattedPrompt;
  };

  const renderQuestionOptions = (question, response) => {
    if (question.type !== 'multiple_choice' && question.type !== 'true_false') {
      return null;
    }

    // Get all selected option IDs for multiple choice (support both new and old format)
    let selectedOptionIds = [];
    if (question.type === 'multiple_choice') {
      // Try to parse from answer_text (new format for multiple selections)
      if (response?.answer_text) {
        try {
          const parsed = JSON.parse(response.answer_text);
          if (Array.isArray(parsed)) {
            selectedOptionIds = parsed;
          }
        } catch (e) {
          // Not JSON, fall through to legacy check
        }
      }
      
      // Fallback to selected_option_id (legacy single selection)
      if (selectedOptionIds.length === 0 && response?.selected_option_id) {
        selectedOptionIds = [response.selected_option_id];
      }
    } else if (question.type === 'true_false') {
      // True/false only has single selection
      if (response?.selected_option_id) {
        selectedOptionIds = [response.selected_option_id];
      }
    }

    return (
      <ReviewOptionsList>
        {question.options.map(option => {
          const isSelected = selectedOptionIds.includes(option.id);
          return (
          <ReviewOption
            key={option.id}
              selected={isSelected}
          >
{handleArabicText(option.option_text)}
          </ReviewOption>
          );
        })}
      </ReviewOptionsList>
    );
  };

  const goToNext = useCallback(async () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      // Save current question before navigating
      await saveCurrentQuestionBeforeNavigation();
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  }, [currentQuestionIndex, quiz, saveCurrentQuestionBeforeNavigation]);

  const goToPrevious = useCallback(async () => {
    if (currentQuestionIndex > 0) {
      // Save current question before navigating
      await saveCurrentQuestionBeforeNavigation();
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  }, [currentQuestionIndex, saveCurrentQuestionBeforeNavigation]);

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <FiClock size={24} />
          <p>Loading quiz...</p>
        </LoadingState>
      </Container>
    );
  }

  if (error) {
    const isAutoSubmitError = error.includes('auto-submit') || error.includes('Failed to auto-submit');
    return (
      <Container>
        <ErrorState>
          <ErrorIcon>
            {isAutoSubmitted ? '✅' : isAutoSubmitError ? '⚠️' : '⚠️'}
          </ErrorIcon>
          <ErrorTitle>
            {isAutoSubmitted ? 'Quiz Auto-Submitted' : isAutoSubmitError ? 'Auto-Submit Failed' : 'Error Loading Quiz'}
          </ErrorTitle>
          <ErrorMessage>
            {error}
          </ErrorMessage>
          {isAutoSubmitted ? (
            <ErrorAction onClick={() => navigate(`/student/result/${attemptId}`)}>
              <FiCheckCircle size={16} />
              View Results
            </ErrorAction>
          ) : isAutoSubmitError ? (
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <ErrorAction onClick={() => handleSubmit(false)} disabled={saving}>
                <FiCheckCircle size={16} />
                Submit Quiz Manually
              </ErrorAction>
              <ErrorAction onClick={() => navigate('/student/quizzes')} style={{ background: 'var(--text-muted)' }}>
                <FiArrowLeft size={16} />
                Back to Quizzes
              </ErrorAction>
            </div>
          ) : (
            <ErrorAction onClick={() => navigate('/student/quizzes')}>
              <FiArrowLeft size={16} />
              Back to Quizzes
            </ErrorAction>
          )}
        </ErrorState>
      </Container>
    );
  }

  if (!quiz || !attempt) {
    return (
      <Container>
        <ErrorState>
          <ErrorIcon>❌</ErrorIcon>
          <ErrorTitle>Quiz Not Found</ErrorTitle>
          <ErrorMessage>
            The quiz you're looking for could not be found or you don't have permission to access it.
          </ErrorMessage>
          <ErrorAction onClick={() => navigate('/student/quizzes')}>
            <FiArrowLeft size={16} />
            Back to Quizzes
          </ErrorAction>
        </ErrorState>
      </Container>
    );
  }

  // CRITICAL FIX: Validate currentQuestionIndex is within bounds
  // This is the final safety check before rendering - catches any edge cases
  if (currentQuestionIndex < 0 || currentQuestionIndex >= quiz.questions.length) {
    logger.error('currentQuestionIndex out of bounds at render time - final safety check', { 
      currentQuestionIndex, 
      questionsLength: quiz.questions.length,
      attemptId,
      scenario: 'Student was on question #' + (currentQuestionIndex + 1) + ' but quiz only has ' + quiz.questions.length + ' questions. This typically happens when a partial retake/edit is granted while the student is on a question outside the allowed set, or the quiz was filtered after the student navigated to that question.'
    });
    // Reset to first question
    if (quiz.questions.length > 0) {
      setCurrentQuestionIndex(0);
      return (
        <Container>
          <LoadingState>
            <FiClock size={24} />
            <p>Loading question...</p>
          </LoadingState>
        </Container>
      );
    } else {
      return (
        <Container>
          <ErrorState>
            <ErrorIcon>❌</ErrorIcon>
            <ErrorTitle>Quiz Has No Questions</ErrorTitle>
            <ErrorMessage>
              This quiz has no questions available. Please contact your instructor.
            </ErrorMessage>
            <ErrorAction onClick={() => navigate('/student/quizzes')}>
              <FiArrowLeft size={16} />
              Back to Quizzes
            </ErrorAction>
          </ErrorState>
        </Container>
      );
    }
  }

  const currentQuestion = quiz.questions[currentQuestionIndex];
  
  // CRITICAL FIX: Double-check currentQuestion exists
  // Use a ref to prevent rapid resets that could cause flickering
  if (!currentQuestion) {
    const now = Date.now();
    // Only reset if we haven't reset in the last 500ms (prevents flickering)
    if (now - lastIndexResetRef.current > 500) {
      logger.error('currentQuestion is undefined at render time - this should not happen if validation worked', { 
        currentQuestionIndex, 
        questionsLength: quiz.questions.length,
        attemptId,
        scenario: 'Question at index ' + currentQuestionIndex + ' is undefined even though index is within bounds. This indicates a data inconsistency.'
      });
      // Reset to first question
      if (quiz.questions.length > 0) {
        lastIndexResetRef.current = now;
        setCurrentQuestionIndex(0);
        return (
          <Container>
            <LoadingState>
              <FiClock size={24} />
              <p>Loading question...</p>
            </LoadingState>
          </Container>
        );
      } else {
        return (
          <Container>
            <ErrorState>
              <ErrorIcon>❌</ErrorIcon>
              <ErrorTitle>Question Not Found</ErrorTitle>
              <ErrorMessage>
                The question you're trying to access could not be found. Please try refreshing the page.
              </ErrorMessage>
              <ErrorAction onClick={() => navigate('/student/quizzes')}>
                <FiArrowLeft size={16} />
                Back to Quizzes
              </ErrorAction>
            </ErrorState>
          </Container>
        );
      }
    } else {
      // If we recently reset, just show loading to prevent flickering
      return (
        <Container>
          <LoadingState>
            <FiClock size={24} />
            <p>Loading question...</p>
          </LoadingState>
        </Container>
      );
    }
  }
  
  const currentResponse = responses[currentQuestion.id];
  const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;

  const getTimerProps = () => {
    if (!timeRemaining) return {};
    if (timeRemaining <= 60) return { danger: true };
    if (timeRemaining <= 300) return { warning: true };
    return {};
  };

  return (
    <Container>
      {/* Network Error Banner */}
      {networkErrorBanner && (
        <NetworkErrorBanner severity={networkErrorBanner.severity}>
          <div className="banner-content">
            {networkErrorBanner.severity === 'critical' && <FiAlertCircle size={20} />}
            {networkErrorBanner.severity === 'warning' && <FiAlertCircle size={20} />}
            {networkErrorBanner.severity === 'info' && <FiCheckCircle size={20} />}
            <span>
              {networkErrorBanner.message}
              {networkErrorBanner.count > 0 && ` (${networkErrorBanner.count} answer${networkErrorBanner.count !== 1 ? 's' : ''})`}
            </span>
          </div>
          <div className="banner-actions">
            {networkErrorBanner.type !== 'restored' && (
              <button
                className="dismiss-button"
                onClick={() => setNetworkErrorBanner(null)}
                title="Dismiss"
              >
                Dismiss
              </button>
            )}
          </div>
        </NetworkErrorBanner>
      )}
      
      {firewallWarning && (
        <NetworkErrorBanner severity={firewallWarning.severity}>
          <div className="banner-content">
            <FiInfo size={20} />
            <span>{firewallWarning.message}</span>
          </div>
          <div className="banner-actions">
            <button
              className="dismiss-button"
              onClick={() => setFirewallWarning(null)}
              title="Dismiss"
            >
              Dismiss
            </button>
          </div>
        </NetworkErrorBanner>
      )}
      
      {/* UI FIX 2: Auto-Submit Status Banner */}
      {isAutoSubmitting && timeRemaining === 0 && (
        <NetworkErrorBanner severity="info" style={{ backgroundColor: 'var(--info-color)', color: 'var(--text-inverse)', borderColor: 'var(--info-color)' }}>
          <div className="banner-content">
            <FiRefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
            <span>
              <strong>Auto-Submitting Quiz...</strong> Please wait while your quiz is being submitted automatically. Do not close this page.
            </span>
          </div>
        </NetworkErrorBanner>
      )}
      
      {/* UI FIX 4: Extended Timer Warning */}
      {timerExtended && timeRemaining !== null && timeRemaining > 0 && timeRemaining <= 3 && (
        <NetworkErrorBanner severity="warning">
          <div className="banner-content">
            <FiInfo size={20} />
            <span>
              Timer extended by 3 seconds to ensure all answers are saved before submission.
            </span>
          </div>
          <div className="banner-actions">
            <button
              className="dismiss-button"
              onClick={() => setTimerExtended(false)}
              title="Dismiss"
            >
              Dismiss
            </button>
          </div>
        </NetworkErrorBanner>
      )}
      
      {/* UI FIX 3: Auto-Submit Overlay */}
      {isAutoSubmitting && (
        <AutoSubmitOverlay>
          {error && (error.includes('auto-submit') || error.includes('taking too long') || error.includes('Submission')) ? (
            <>
              <FiAlertCircle size={48} style={{ color: 'var(--text-inverse)' }} />
              <div className="message" style={{ color: 'var(--text-inverse)' }}>Submission Failed</div>
              <div className="submessage" style={{ color: 'var(--text-inverse)', maxWidth: '500px', textAlign: 'center' }}>
                {error}
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button
                  onClick={() => {
                    setIsAutoSubmitting(false);
                    setError(null);
                    handleSubmit(false);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: 'var(--background-primary)',
                    color: 'var(--error-color)',
                    border: 'none',
                    borderRadius: 0,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                >
                  Submit Manually
                </button>
                <button
                  onClick={() => {
                    setIsAutoSubmitting(false);
                    setError(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: 'transparent',
                    color: 'var(--text-inverse)',
                    border: '2px solid var(--text-inverse)',
                    borderRadius: 0,
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                >
                  Dismiss
                </button>
              </div>
            </>
          ) : (
            <>
              <FiRefreshCw size={48} className="spinner" style={{ color: 'var(--text-inverse)' }} />
              <div className="message">Submitting Quiz...</div>
              <div className="submessage">Please wait, do not close this page</div>
            </>
          )}
        </AutoSubmitOverlay>
      )}
      
      {isReviewMode ? (
        // Review Mode - Show only review content
        (<ReviewContainer>
          <ReviewHeader>
              {timeRemaining !== null && (
              <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                <Timer {...getTimerProps()}>
                  <FiClock size={16} />
                  Quiz Timer: {timeRemaining === 0 ? (
                    <TimerExpiredText>Time Expired - Submitting...</TimerExpiredText>
                  ) : (
                    quizApiService.formatTimeRemaining(timeRemaining)
                  )}
                </Timer>
                {timeRemaining > 0 && timeRemaining <= 3 && timerExtended && (
                  <span style={{ fontSize: '0.875rem', color: 'var(--warning-color)', fontWeight: 'bold' }}>
                    Extended timer: {timeRemaining} seconds remaining
                  </span>
                )}
                {window.quizClockSkewOffset && Math.abs(window.quizClockSkewOffset) > 2000 && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Timer synced with server time
                  </span>
                )}
            </div>
            )}
            <ReviewTitle>Review Your Answers</ReviewTitle>
            <ReviewSubtitle>
              Please review all your answers before submitting. You can go back to edit any question.
            </ReviewSubtitle>
          </ReviewHeader>
          {quiz.questions.map((question, index) => {
            const response = responses[question.id];
            const answerText = formatAnswerForReview(question, response);
            const isAnswered = isQuestionAnswered(question, response);
            
            return (
              <ReviewQuestion key={question.id} unanswered={!isAnswered}>
                <ReviewQuestionHeader>
                  <div>
                    {question.extra_credit && (
                      <ExtraCreditBadge>Extra Credit</ExtraCreditBadge>
                    )}
                  </div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem',
                    flexWrap: 'wrap'
                  }}>
                    <EditLink onClick={() => goToQuestionFromReview(index)}>
                      Edit Answer
                    </EditLink>
                    <ReviewQuestionPoints>{question.points} point{question.points !== 1 ? 's' : ''}</ReviewQuestionPoints>
                    {saveStatuses[question.id] && (
                      <span style={{
                        fontSize: '0.75rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        ...(saveStatuses[question.id].status === 'saving' && {
                          backgroundColor: 'rgba(246, 224, 94, 0.1)',
                          color: 'var(--warning-dark)'
                        }),
                        ...(saveStatuses[question.id].status === 'saved' && {
                          backgroundColor: 'rgba(104, 211, 145, 0.1)',
                          color: 'var(--success-dark)'
                        }),
                        ...(saveStatuses[question.id].status === 'failed' && {
                          backgroundColor: 'rgba(252, 129, 129, 0.1)',
                          color: 'var(--error-color)'
                        })
                      }}>
                        {saveStatuses[question.id].status === 'saving' && '⏳ Saving...'}
                        {saveStatuses[question.id].status === 'saved' && '✓ Saved'}
                        {saveStatuses[question.id].status === 'failed' && '⚠ Failed'}
                      </span>
                    )}
                  </div>
                </ReviewQuestionHeader>
                
                {question.type === 'fill_blank' && response && response.answer_text ? (
                  <ReviewQuestionPromptWithHTML
dangerouslySetInnerHTML={{
                      __html: sanitizeHtmlForViewer(`${index + 1}. ${formatFillBlankPromptWithAnswers(handleArabicText(question.prompt), response)
                        .replace(/\[([^\]]+)\]/g, '<span class="fill-blank-answer">$1</span>')}`)
                    }}
                  />
                ) : (
                  <ReviewQuestionPrompt>
{index + 1}. {handleArabicText(question.prompt)}
                  </ReviewQuestionPrompt>
                )}
                
                <ReviewAnswer>
                  {(question.type === 'multiple_choice' || question.type === 'true_false') ? (
                    <>
                      {renderQuestionOptions(question, response)}
                      {!isAnswered && (
                        <ReviewAnswerEmpty>No option selected</ReviewAnswerEmpty>
                      )}
                    </>
                  ) : question.type === 'fill_blank' ? (
                    !isAnswered && (
                      <ReviewAnswerEmpty>
                        {(() => {
                          const blankCount = getBlankCount(question.prompt);
                          if (blankCount > 1) {
                            const emptyBlanks = [];
                            for (let i = 0; i < blankCount; i++) {
                              if (!isBlankFilled(response, i)) {
                                emptyBlanks.push(i + 1);
                              }
                            }
                            return emptyBlanks.length > 0 
                              ? `Enter a response for Blank Answer ${emptyBlanks.join(', Blank Answer ')}`
                              : 'No answer provided';
                          }
                          return 'No answer provided';
                        })()}
                      </ReviewAnswerEmpty>
                    )
                  ) : question.type === 'matching' ? (
                    isAnswered && answerText ? (
                      <ReviewAnswerText>
                        {answerText.split('\n').map((line, i) => (
                          <div key={i} style={{ marginBottom: '0.5rem' }}>{line}</div>
                        ))}
                      </ReviewAnswerText>
                    ) : (
                      <ReviewAnswerEmpty>No answer provided</ReviewAnswerEmpty>
                    )
                  ) : (
                    answerText ? (
                      <ReviewAnswerText>
                        {answerText.split('\n').map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                      </ReviewAnswerText>
                    ) : (
                      <ReviewAnswerEmpty>No answer provided</ReviewAnswerEmpty>
                    )
                  )}
                </ReviewAnswer>
              </ReviewQuestion>
            );
          })}
          <ReviewActions>
            <ReviewButton className="secondary" onClick={exitReviewMode}>
              <FiArrowLeft size={16} />
              Back to Quiz
            </ReviewButton>
            <ReviewButton 
              className="success" 
              onClick={handleSubmit}
              disabled={saving || hasUnsavedAnswers || isAutoSubmitting || (attempt?.course_id && attempt?.assignment?.course_is_active === false)}
              title={
                attempt?.course_id && attempt?.assignment?.course_is_active === false
                  ? 'Course is inactive - submissions are disabled'
                  : isAutoSubmitting ? 'Auto-submitting...' 
                  : hasUnsavedAnswers ? `Saving ${unsavedCount} answer(s)...` 
                  : saving ? 'Submitting quiz...' 
                  : 'Submit quiz'
              }
            >
              <FiCheckCircle size={16} />
              {saving 
                ? 'Submitting...' 
                : hasUnsavedAnswers 
                  ? (unsavedCount > 0 
                      ? `Saving ${unsavedCount} answer(s)...`
                      : 'Saving answers...')
                  : 'Submit Quiz'}
            </ReviewButton>
          </ReviewActions>
        </ReviewContainer>)
      ) : (
        // Quiz Mode - Show quiz content
        (<>
          <Header>
            <div>
              <QuizTitle>{quiz.title}</QuizTitle>
              {quiz.description && <QuizDescription>{quiz.description}</QuizDescription>}
              
              {/* Quiz Details Section */}
              {attempt?.course_id && attempt?.assignment?.course_is_active === false && (
                <div style={{ 
                  padding: '1rem', 
                  marginBottom: '1rem', 
                  backgroundColor: 'rgba(252, 129, 129, 0.1)', 
                  border: '1px solid var(--error-color)',
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <FiAlertCircle size={20} color="var(--error-color)" />
                  <span style={{ color: 'var(--error-color)', fontWeight: '500' }}>
                    <strong>This course is currently inactive.</strong> Quiz submissions are not allowed for inactive courses.
                  </span>
                </div>
              )}
              <QuizDetails>
                <DetailItem>
                  <FiBookOpen size={16} />
                  <span><strong>Course:</strong> {attempt?.assignment?.course_name || quiz?.course_name || attempt?.course_name || 'N/A'}</span>
                </DetailItem>
                <DetailItem>
                  <FiUser size={16} />
                  <span><strong>Instructor:</strong> {getInstructorName(attempt?.assignment) || getInstructorName(quiz) || getInstructorName(attempt) || 'N/A'}</span>
                </DetailItem>
                <DetailItem>
                  <FiFileText size={16} />
                  <span><strong>Questions:</strong> {quiz.questions.length}</span>
                </DetailItem>
                <DetailItem>
                  <FiTime size={16} />
                  <span><strong>Time Limit:</strong> {attempt?.assignment?.time_limit_minutes ? `${attempt.assignment.time_limit_minutes} min` : 'No time limit'}</span>
                </DetailItem>
                <DetailItem>
                  <FiFileText size={16} />
                  <span><strong>Attempts:</strong> {attempt?.assignment?.attempts_allowed || 1} allowed</span>
                </DetailItem>
                {dueDate && (
                  <DetailItem>
                    <FiCalendar size={16} />
                    <span><strong>Due:</strong> {formatDueDate(dueDate)}</span>
                  </DetailItem>
                )}
                
                {/* Status indicators integrated into the details grid */}
                {autoSaveEnabled && timeRemaining === null && !autoSaveFailed && (
                  <DetailItem>
                    <FiSave size={16} />
                    <span>Auto-save enabled</span>
                  </DetailItem>
                )}
                {autoSaveFailed && (
                  <DetailItem>
                    <FiSave size={16} />
                    <span style={{ color: 'var(--error-color)' }}>⚠️ Auto-save failed - Use "Save Answer" button</span>
                  </DetailItem>
                )}
                {timeRemaining !== null && (
                  <DetailItem>
                    <FiClock size={16} />
                    <span>
                      <strong>Timer:</strong>{' '}
                      {timeRemaining === 0 ? (
                        <TimerExpiredText>Time Expired - Submitting...</TimerExpiredText>
                      ) : (
                        <strong>{quizApiService.formatTimeRemaining(timeRemaining)}</strong>
                      )}
                      {timeRemaining > 0 && timeRemaining <= 3 && timerExtended && (
                        <span style={{ fontSize: '0.875rem', color: 'var(--warning-color)', fontWeight: 'bold', marginLeft: '0.5rem' }}>
                          (Extended: {timeRemaining}s)
                        </span>
                      )}
                    </span>
                  </DetailItem>
                )}
                
                {/* Network Status Indicator */}
                <DetailItem>
                  <NetworkStatusIndicator status={networkStatus}>
                    {networkStatus === 'offline' && <FiAlertCircle size={16} />}
                    {networkStatus === 'unstable' && <FiAlertCircle size={16} />}
                    {networkStatus === 'online' && <FiCheckCircle size={16} />}
                    <span>
                      <strong>Portal Status:</strong>{' '}
                      {networkStatus === 'offline' && 'Offline - Answers Queued'}
                      {networkStatus === 'unstable' && 'Unstable Connection'}
                      {networkStatus === 'online' && 'Online'}
                    </span>
                  </NetworkStatusIndicator>
                </DetailItem>
              </QuizDetails>
            </div>
          </Header>
          <ProgressBar>
            <ProgressFill $progress={progress} />
          </ProgressBar>
          <QuestionCard>
            <QuestionHeader ref={questionHeaderRef}>
              <QuestionNumber>
                Question {currentQuestionIndex + 1} of {quiz.questions.length}
                {currentQuestion.extra_credit && (
                  <ExtraCreditBadge>Extra Credit</ExtraCreditBadge>
                )}
              </QuestionNumber>
              <QuestionPoints>
                {currentQuestion.points} point{currentQuestion.points !== 1 ? 's' : ''}
              </QuestionPoints>
            </QuestionHeader>
            
            {/* Save Status Indicator */}
            {saveStatuses[currentQuestion.id] && (
              <SaveStatusIndicator status={saveStatuses[currentQuestion.id].status}>
                {saveStatuses[currentQuestion.id].status === 'saving' && (
                  <>
                    <FiClock size={14} />
                    <span>Saving answer...</span>
                  </>
                )}
                {saveStatuses[currentQuestion.id].status === 'saved' && (
                  <>
                    <FiCheckCircle size={14} />
                    <span>Answer saved</span>
                  </>
                )}
                {saveStatuses[currentQuestion.id].status === 'failed' && (
                  <>
                    <FiAlertCircle size={14} />
                    <span>Failed to save answer</span>
                    <RetryButton
                      onClick={async () => {
                        const response = responses[currentQuestion.id];
                        if (response && saveManagerRef.current) {
                          await saveManagerRef.current.saveResponse(currentQuestion.id, response);
                        }
                      }}
                      title="Retry saving this answer"
                    >
                      <FiRefreshCw size={12} />
                      Retry
                    </RetryButton>
                  </>
                )}
              </SaveStatusIndicator>
            )}

<QuestionPrompt>
              {currentQuestion.type === 'fill_blank' ? formatFillBlankPrompt(handleArabicText(currentQuestion.prompt)) : handleArabicText(currentQuestion.prompt)}
            </QuestionPrompt>

            {currentQuestion.type === 'multiple_choice' && (
              <OptionList>
                {/* Show instruction if there are multiple correct answers */}
                {currentQuestion.options.filter(opt => opt.is_correct).length > 1 && (
                  <OptionInstruction>
                    You can select multiple answers. Check all that apply.
                  </OptionInstruction>
                )}
                {currentQuestion.options.map(option => {
                  // Check if this option is selected (support both old and new format)
                  let isSelected = false;
                  
                  // Try to parse from answer_text (new format for multiple selections)
                  if (currentResponse?.answer_text) {
                    try {
                      const parsed = JSON.parse(currentResponse.answer_text);
                      if (Array.isArray(parsed)) {
                        isSelected = parsed.includes(option.id);
                      }
                    } catch (e) {
                      // Not JSON, fall through to legacy check
                    }
                  }
                  
                  // Fallback to selected_option_id (legacy single selection)
                  if (!isSelected) {
                    isSelected = currentResponse?.selected_option_id === option.id;
                  }
                  
                  return (
                    <OptionItem
                      key={option.id}
                      className={isSelected ? 'selected' : ''}
                    >
                      <OptionInput
                        type="checkbox"
                        checked={isSelected}
                        disabled={isAutoSubmitting}
                        onChange={() => handleResponseChange(currentQuestion.id, option.id, 'multiple_choice')}
                      />
<OptionText>{handleArabicText(option.option_text)}</OptionText>
                    </OptionItem>
                  );
                })}
              </OptionList>
            )}

            {currentQuestion.type === 'true_false' && (
              <OptionList>
                {currentQuestion.options.map(option => {
                  return (
                    <OptionItem
                      key={option.id}
                      className={currentResponse?.selected_option_id === option.id ? 'selected' : ''}
                    >
                      <OptionInput
                        type="radio"
                        name={`question_${currentQuestion.id}`}
                        checked={currentResponse?.selected_option_id === option.id}
                        disabled={isAutoSubmitting}
                        onChange={() => handleResponseChange(currentQuestion.id, option.id, 'true_false')}
                      />
<OptionText>{handleArabicText(option.option_text)}</OptionText>
                    </OptionItem>
                  );
                })}
              </OptionList>
            )}

            {currentQuestion.type === 'fill_blank' && (() => {
              const blankCount = getBlankCount(currentQuestion.prompt);
              
              if (blankCount === 1) {
                // Single blank - use the original single input
                return (
                  <FillBlankInput
                    type="text"
                    value={currentResponse?.answer_text || ''}
                    disabled={isAutoSubmitting}
                    onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value, 'fill_blank')}
                    placeholder="Enter your answer..."
                  />
                );
              } else {
                // Multiple blanks - show separate inputs for each
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {Array.from({ length: blankCount }, (_, index) => {
                      return (
                        <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <label style={{ 
                            fontSize: '0.875rem', 
                            fontWeight: '500', 
                            color: 'var(--text-primary)'
                          }}>
                            Blank Answer {index + 1}:
                          </label>
                          <FillBlankInput
                            type="text"
                            value={currentResponse?.answer_texts?.[index] || ''}
                            disabled={isAutoSubmitting}
                            onChange={(e) => handleResponseChange(currentQuestion.id, e.target.value, 'fill_blank', index)}
                            placeholder={`Enter answer for blank ${index + 1}...`}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              }
            })()}

            {currentQuestion.type === 'matching' && (() => {
              const settings = currentQuestion.settings || {};
              const leftItems = settings.leftItems || [];
              const rightItems = settings.rightItems || [];
              
              // Shuffle arrays for display (deterministic based on question ID)
              const shuffleWithMapping = (array, seed) => {
                // Filter out empty items and create mapping
                const validItems = [];
                const originalToValid = {}; // originalIndex -> validIndex
                array.forEach((item, originalIndex) => {
                  if (item && item.trim()) {
                    originalToValid[originalIndex] = validItems.length;
                    validItems.push({ item, originalIndex });
                  }
                });
                
                if (validItems.length === 0) {
                  return { shuffledArray: [], shuffleMapping: {}, reverseMapping: {} };
                }
                
                // Create array of valid indices to shuffle
                const validIndices = validItems.map((_, i) => i);
                const shuffledValidIndices = [...validIndices];
                
                // Use a simple seeded random number generator
                let seedValue = seed;
                const seededRandom = () => {
                  seedValue = (seedValue * 9301 + 49297) % 233280;
                  return seedValue / 233280;
                };
                
                // Fisher-Yates shuffle with seeded random
                for (let i = shuffledValidIndices.length - 1; i > 0; i--) {
                  const j = Math.floor(seededRandom() * (i + 1));
                  [shuffledValidIndices[i], shuffledValidIndices[j]] = [shuffledValidIndices[j], shuffledValidIndices[i]];
                }
                
                // Create shuffled array and mapping
                const shuffledArray = shuffledValidIndices.map(validIndex => validItems[validIndex].item);
                const shuffleMapping = {}; // shuffledIndex -> originalIndex
                const reverseMapping = {}; // originalIndex -> shuffledIndex
                
                shuffledValidIndices.forEach((validIndex, shuffledIndex) => {
                  const originalIndex = validItems[validIndex].originalIndex;
                  shuffleMapping[shuffledIndex] = originalIndex;
                  reverseMapping[originalIndex] = shuffledIndex;
                });
                
                return { shuffledArray, shuffleMapping, reverseMapping };
              };
              
              // Use question ID as seed for deterministic shuffling
              const seed = currentQuestion.id || 0;
              const leftShuffle = shuffleWithMapping(leftItems, seed);
              const rightShuffle = shuffleWithMapping(rightItems, seed + 1000); // Different seed for right items
              
              const shuffledLeftItems = leftShuffle.shuffledArray;
              const shuffledRightItems = rightShuffle.shuffledArray;
              const leftShuffleMapping = leftShuffle.shuffleMapping;
              const rightShuffleMapping = rightShuffle.shuffleMapping;
              const rightReverseMapping = rightShuffle.reverseMapping;
              
              // Parse current response pairs (using original indices)
              let currentPairs = [];
              try {
                if (currentResponse?.answer_text) {
                  currentPairs = JSON.parse(currentResponse.answer_text);
                }
              } catch (e) {
                currentPairs = [];
              }
              
              // Convert original indices to shuffled indices for display
              const getShuffledRightIndexForShuffledLeft = (shuffledLeftIndex) => {
                const originalLeftIndex = leftShuffleMapping[shuffledLeftIndex];
                const pair = currentPairs.find(p => p.leftIndex === originalLeftIndex);
                if (pair) {
                  // Convert original rightIndex to shuffled rightIndex
                  return rightReverseMapping[pair.rightIndex];
                }
                return null;
              };
              
              return (
                <div style={{ marginTop: '1rem' }}>
                  <MatchingHeaderGrid>
                    <div>
                      <h4 style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        marginBottom: '1rem',
                        color: 'var(--text-primary)',
                        lineHeight: '1.4'
                      }}>
                        Terms
                      </h4>
                    </div>
                    <div>
                      <h4 style={{ 
                        fontSize: '1rem', 
                        fontWeight: '600', 
                        marginBottom: '1rem',
                        color: 'var(--text-primary)',
                        lineHeight: '1.4'
                      }}>
                        Definitions
                      </h4>
                    </div>
                  </MatchingHeaderGrid>
                  
                  {shuffledLeftItems.map((leftItem, shuffledLeftIndex) => {
                    if (!leftItem || !leftItem.trim()) return null;
                    
                    // Get original leftIndex from shuffled index
                    const originalLeftIndex = leftShuffleMapping[shuffledLeftIndex];
                    const selectedShuffledRightIndex = getShuffledRightIndexForShuffledLeft(shuffledLeftIndex);
                    
                    return (
                      <MatchingItemGrid key={shuffledLeftIndex}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          fontWeight: '500',
                          color: 'var(--text-primary)',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'auto',
                          minWidth: 0, // Allows flex item to shrink below content size
                          flex: '1 1 0%' // Flexible sizing
                        }}>
                          {leftItem}
                        </div>
                        <CustomDropdown 
                          data-dropdown
                          ref={el => {
                            const dropdownKey = `${currentQuestion.id}-${shuffledLeftIndex}`;
                            dropdownRefs.current[dropdownKey] = el;
                          }}
                        >
                          <DropdownButton
                            type="button"
                            hasValue={selectedShuffledRightIndex !== null}
                            disabled={isAutoSubmitting}
                            onClick={() => {
                              if (isAutoSubmitting) return; // UI FIX: Prevent interaction during auto-submit
                              const dropdownKey = `${currentQuestion.id}-${shuffledLeftIndex}`;
                              const button = dropdownRefs.current[dropdownKey]?.querySelector('button');
                              if (button) {
                                const rect = button.getBoundingClientRect();
                                const isMobile = window.innerWidth <= 768;
                                const isSmallMobile = window.innerWidth <= 480;
                                setDropdownPositions(prev => ({
                                  ...prev,
                                  [dropdownKey]: {
                                    top: rect.bottom + window.scrollY + 4,
                                    left: isMobile ? Math.max(8, rect.left + window.scrollX - 8) : rect.left + window.scrollX,
                                    width: isMobile 
                                      ? (isSmallMobile 
                                          ? Math.min(rect.width, window.innerWidth - 8) 
                                          : Math.min(rect.width, window.innerWidth - 16))
                                      : rect.width
                                  }
                                }));
                              }
                              setOpenDropdowns(prev => ({
                                ...prev,
                                [dropdownKey]: !prev[dropdownKey]
                              }));
                            }}
                          >
                            {selectedShuffledRightIndex !== null 
                              ? (shuffledRightItems[selectedShuffledRightIndex] || `Definition ${selectedShuffledRightIndex + 1}`)
                              : 'Select a match...'}
                            <DropdownIcon>
                              {openDropdowns[`${currentQuestion.id}-${shuffledLeftIndex}`] ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
                            </DropdownIcon>
                          </DropdownButton>
                          {openDropdowns[`${currentQuestion.id}-${shuffledLeftIndex}`] && createPortal(
                            <DropdownMenu 
                              data-dropdown
                              position={dropdownPositions[`${currentQuestion.id}-${shuffledLeftIndex}`]}
                            >
                              <DropdownOption
                                type="button"
                                onClick={() => {
                                  // Convert shuffled indices back to original indices
                                  handleResponseChange(
                                    currentQuestion.id, 
                                    { leftIndex: originalLeftIndex, rightIndex: null }, 
                                    'matching'
                                  );
                                  setOpenDropdowns(prev => {
                                    const newState = { ...prev };
                                    delete newState[`${currentQuestion.id}-${shuffledLeftIndex}`];
                                    return newState;
                                  });
                                }}
                              >
                                {selectedShuffledRightIndex !== null ? 'Clear selection' : 'Select a match...'}
                              </DropdownOption>
                              {shuffledRightItems.map((rightItem, shuffledRightIndex) => {
                                if (!rightItem || !rightItem.trim()) return null;
                                // Get original rightIndex from shuffled index
                                const originalRightIndex = rightShuffleMapping[shuffledRightIndex];
                                return (
                                  <DropdownOption
                                    key={shuffledRightIndex}
                                    type="button"
                                    className={selectedShuffledRightIndex === shuffledRightIndex ? 'selected' : ''}
                                    onClick={() => {
                                      // Convert shuffled indices back to original indices for saving
                                      handleResponseChange(
                                        currentQuestion.id, 
                                        { leftIndex: originalLeftIndex, rightIndex: originalRightIndex }, 
                                        'matching'
                                      );
                                      setOpenDropdowns(prev => {
                                        const newState = { ...prev };
                                        delete newState[`${currentQuestion.id}-${shuffledLeftIndex}`];
                                        return newState;
                                      });
                                    }}
                                  >
                                    {rightItem || `Definition ${shuffledRightIndex + 1}`}
                                  </DropdownOption>
                                );
                              })}
                            </DropdownMenu>,
                            document.body
                          )}
                        </CustomDropdown>
                      </MatchingItemGrid>
                    );
                  })}
                </div>
              );
            })()}

            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <SaveButton
                onClick={() => {
                  logger.debug('Save Button clicked', { questionId: currentQuestion.id, hasResponse: !!currentResponse });
                  saveResponse(currentQuestion.id);
                }}
                disabled={saving}
                className={currentResponse ? 'saved' : ''}
              >
                <FiSave size={14} />
                {saving ? 'Saving...' : 'Save Answer'}
              </SaveButton>
            </div>
          </QuestionCard>
          <Navigation>
            <NavButton
              onClick={goToPrevious}
              disabled={currentQuestionIndex === 0}
            >
              <FiArrowLeft size={16} />
              Previous
            </NavButton>

            <PaginationContainer>
              {quiz.questions.map((question, index) => {
                const questionResponse = responses[question.id];
                const hasVisited = questionResponse !== undefined; // Question has been visited if response exists (even if empty)
                const isAnswered = hasVisited ? isQuestionAnswered(question, questionResponse) : null;
                const saveStatus = saveStatuses[question.id];
                
                return (
                <PaginationButton
                  key={index}
                  onClick={() => goToQuestion(index)}
                  $active={index === currentQuestionIndex}
                    $visited={hasVisited}
                    $answered={isAnswered}
                    title={
                      saveStatus?.status === 'saving' ? 'Saving...' :
                      saveStatus?.status === 'saved' ? 'Answer saved' :
                      saveStatus?.status === 'failed' ? 'Failed to save - Click to retry' :
                      hasVisited ? (isAnswered ? 'Answered' : 'Visited') :
                      'Not visited'
                    }
                    style={{
                      position: 'relative',
                      ...(saveStatus?.status === 'saving' && {
                        borderColor: 'var(--warning-color)',
                        backgroundColor: 'rgba(246, 224, 94, 0.1)'
                      }),
                      ...(saveStatus?.status === 'saved' && {
                        borderColor: 'var(--success-color)',
                        backgroundColor: 'rgba(104, 211, 145, 0.1)'
                      }),
                      ...(saveStatus?.status === 'failed' && {
                        borderColor: 'var(--error-color)',
                        backgroundColor: 'rgba(252, 129, 129, 0.1)',
                        animation: 'pulse 2s infinite'
                      })
                    }}
                >
                  {index + 1}
                </PaginationButton>
                );
              })}
            </PaginationContainer>

            {currentQuestionIndex === quiz.questions.length - 1 ? (
              <NavButton
                onClick={enterReviewMode}
                disabled={saving || hasUnsavedAnswers || isAutoSubmitting || (attempt?.course_id && attempt?.assignment?.course_is_active === false)}
                className="success"
                title={
                  attempt?.course_id && attempt?.assignment?.course_is_active === false
                    ? 'Course is inactive - submissions are disabled'
                    : isAutoSubmitting ? 'Auto-submitting...' 
                    : hasUnsavedAnswers ? `Saving ${unsavedCount} answer(s)...` 
                    : saving ? 'Submitting...' 
                    : 'Review your answers before submitting'
                }
              >
                <FiCheckCircle size={16} />
                {hasUnsavedAnswers 
                  ? (unsavedCount > 0 
                      ? `Saving ${unsavedCount}...`
                      : 'Saving...')
                  : 'Review Answers'}
              </NavButton>
            ) : (
              <NavButton
                onClick={goToNext}
                disabled={currentQuestionIndex === quiz.questions.length - 1}
                className="primary"
              >
                Next
                <FiArrowRight size={16} />
              </NavButton>
            )}
          </Navigation>
        </>)
      )}
      
      
    </Container>
  );
}

export default QuizPlayer;
